import { useMsal } from "@azure/msal-react";
import { ConstrainMode, DetailsList, DetailsListLayoutMode, IColumn, ICommandBarItemProps, IContextualMenuItem, Label, ProgressIndicator, SelectionMode, Stack, Text } from "@fluentui/react";
import { RouterParams } from "App";
import { RootState } from "app/store";
import DocumentManagement from "components/controls/DocumentManagement";
import { onRenderColumnCurrency } from "components/controls/Grid";
import OptionControl, { IBooleanToggleOption } from "components/controls/OptionControl";
import ScreenSplitter from "components/controls/ScreenSplitter";
import { EValidationSeverity } from "components/controls/ValidationView/ValidationView.type";
import { emitCustomEvent, FieldsToLookupByMasterAttributeId } from "global/Application";
import { loadCheckListAction } from "global/Application/dataActions";
import { useAuthentication } from "global/Authentication";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { setProcessing } from "global/Reducer/statusSlice";
import { ArrayToLookupByPredicate, formatDate, Memoize, parseDate } from "global/Utility/TSFunctions";
import * as IXrmOptions from "model/AttributeOptionSets.type";
import * as IXrmTypes from "model/CRM";
import { IAccountExtended, Ibcbs_groupconfigurationsnapshot_extended } from "model/CRM.type";
import * as FAM from "model/FlexibleAttributeModel";
import { ICheckList } from "model/ICheckList";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Prompt, useParams } from "react-router";
import BlueRelationshipContext from "views/BlueRelationship/BlueRelationshipContext";
import ValidationDialog from "views/BlueRelationship/ValidationDialog";
import { EValidationState, IValidationModel } from "views/BlueRelationship/ValidationDialog/ValidationDialog.type";
import CommentDialog from "../CommentDialog/CommentDialog";
import { ICheckListData } from "../ICheckListData";
import { IDeductible } from "../IDeductible";
import CheckBoxWithTextField from "./CheckBoxWithTextField";
import Checkbox from "./Checkbox";
import CustomPanel from "./CustomPanel";
import { IQResponse, IQResponses } from "./ICheckListQuestions";
import TextField from "./TextField";
import { saveAs } from "file-saver";

interface ICheckListItemProps {

}

interface ISaveTransaction {
    checklist?: ICheckList,
    bcbs_emchecklist_updates?: Partial<IXrmTypes.bcbs_emchecklist>;
    pendingComment?: boolean,
    validation?: TValidationModel
    submit: boolean;

}

interface IValidationData {

}

type TValidationModel = IValidationModel<IValidationData>;

// TODO add applicationinsights logging

const CheckListItem: React.FunctionComponent<ICheckListItemProps> = (props) => {

    const authentication = useAuthentication();
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const appDataSource = new AppDataSource(tokenSource);

    const { bcbs_emchecklistid } = useParams<RouterParams>();

    const state = useSelector((state: RootState) => state);

    const [documentFile, setDocumentFile] = useState<string | null>(null);

    const [checklist, setChecklist] = useState<ICheckList | null>(null);
    const [saveTransaction, setSaveTransaction] = useState<ISaveTransaction | null>(null);

    const [isOpenDoc, setIsOpenDoc] = useState(false);


    const [pdfLoading, setPdfLoading] = useState(false);

    const [checklistLoading, setChecklistLoading] = useState(false);

    const [detachedViewer, setDetachedViewer] = useState(false);

    const [mergedDocument, setMergedDocument] = useState<Blob | undefined>(undefined);
    const refNewWindow = useRef<Window | null>(null);
    const systemUser = authentication.principal?.systemUser!;

    const [checkpointOn, setCheckpointOn] = useState(new Date().getTime());

    const optionsYesNo = useMemo(() => {
        const options: IBooleanToggleOption[] = [];
        options.push({ key: 'No', text: 'No', data: { type: 'bool', value: false } });
        options.push({ key: 'Yes', text: 'Yes', data: { type: 'bool', value: true } });
        return options;
    }, []);

    const [renderIndex, setRenderIndex] = useState(1);

    const deductibleColumns: IColumn[] = useMemo(() => {
        const columns: IColumn[] = [];

        columns.push({
            key: 'name',
            name: 'Tier',
            fieldName: 'name',
            minWidth: 100,
            isResizable: true,
        })

        const fields = ['individualDeductible', 'familyDeductible', 'individualGlobalOOP', 'familyGlobalOOP', 'individualStandardOOP', 'familyStandardOOP',]

        const fieldNameMapping: Record<string, string> = {
            individualDeductible: 'Ind Ded',
            familyDeductible: 'Fam Ded',
            individualGlobalOOP: 'Global Ind OOP',
            familyGlobalOOP: 'Global Fam OOP',
            individualStandardOOP: 'Standard Ind OOP',
            familyStandardOOP: 'Standard Fam OOP',
        }

        fields.forEach(eachField => {
            const column: IColumn = {
                key: eachField,
                name: fieldNameMapping[eachField],
                fieldName: eachField,
                minWidth: 100,
                isResizable: true,
                onRender: onRenderColumnCurrency,
            };

            columns.push(column);
        });
        return columns;
    }, []);

    // postMessage handler
    useEffect(() => {
        function onMessage(event: MessageEvent) {
            if (event.origin !== window.location.origin)
                return;

            if (event.data.blob) {
                setMergedDocument(event.data.blob);
            }
        }

        window.addEventListener?.("message", onMessage, false);

        return () => {
            window.removeEventListener("message", onMessage);
        }
    }, []);


    const productsLookup = useMemo(() => {
        let ppoProducts: Record<string, boolean> = {
            "Consumer Driven Health Plan (CDHP)": true,
            "Health Reimbursement Account (HRA)": true,
            "Health Savings Account (HSA)": true,
            "Integrated Rx w/o Mail Order": true,
            "Integrated Rx with Mail Order": true,
            "Integrated Rx without mail order": true,
            "Level Funded PPO": true,
            "PPO": true,
            "Preferred Blue": true,
            "QHDHP (Qualified High Deductible Health Plan)": true,
            "Tiered PPO": true,
        }

        let epoProducts: Record<string, boolean> = {
            "Exclusive Provider Organization (EPO)": true,
            "EPO (Limited OON)": true,
            "Tiered EPO": true,
            "Tiered EPO (Limited OON)": true,
        }

        let unset = {
            "Blue Rx with mail order MG Groups Only": true,
            "BlueRx without mail order MG Groups Only": true,
            "Chamber": true,
            "Flexible Spending Account (FSA)": true,
            "Freestanding Vision": true,
            "HMO": true,
            "Med_Product": true,
            "Pharmacy Carve-In": true,
            "True Blue Marketplace": true,
            "Vision under Health": true,
        }

        return { ppo: ppoProducts, epo: epoProducts, unset };
    }, []);


    const rebuildChecklist = async () => {

        const checkList = await (async () => {
            const bcbs_emchecklist = await appDataSource.get_bcbs_emchecklist(bcbs_emchecklistid!);
            const checklist: ICheckList = {
                bcbs_emchecklist: bcbs_emchecklist,
                blob: null as any,
                blobEtag: null,
                versionNumber: bcbs_emchecklist.versionnumber,

            }

            return checklist;
        })();

        if (!state?.data?.json?.plans)
            return;

        const plan = state.data.json.plans.find(t => t.id === checkList?.bcbs_emchecklist.bcbs_planid)!;

        if (!plan)
            return;

        const snapshot = state.data.snapshot! as Ibcbs_groupconfigurationsnapshot_extended;
        const account = state.data.account! as IAccountExtended;

        const ret: ICheckList = {
            ...checkList,
            blob: {
                data: {
                    clientName: account.name,
                    admin: {
                        type: 'entity',
                        entityType: 'systemuser',
                        id: systemUser.systemuserid!,
                        name: systemUser.fullname!,
                    },
                    serviceRep: {
                        type: 'entity',
                        entityType: 'systemuser',
                        id: snapshot._createdby_value!,
                        name: snapshot[`_createdby_value@OData.Community.Display.V1.FormattedValue`],
                    },
                    snapshotCreatedBy: {
                        type: 'entity',
                        entityType: 'systemuser',
                        id: snapshot._createdby_value!,
                        name: snapshot[`_createdby_value@OData.Community.Display.V1.FormattedValue`],
                    },
                    plan: { id: plan.id, name: plan.name },
                    bcbs_effectivedate: state.data.snapshot.bcbs_effectivedate,
                    lineOfBusiness: state.data.json.lineOfBusiness ?? undefined,
                    fundingType: state.data.json.fundingType ?? undefined,
                    parentaccount: account._parentaccountid_value
                        ? {
                            id: account._parentaccountid_value,
                            name: account['_parentaccountid_value@OData.Community.Display.V1.FormattedValue']
                        }
                        : null,
                    bcbs_nia_programs_split: [],
                    network_alphaprefixes: [],
                    deductibles: [],
                },
                input: {
                    cardType: '',
                    relayBox: undefined,
                    Q1: {},
                    Q2: {},
                    Q3: {},
                    Q4: {},
                    Q5: {},
                    Q6: {},
                    Q7: {},
                    Q8: {},
                    Q9: {},
                    Q10: {},
                    Q11: {},
                    Q12: {},
                    Q13: {},
                    Q14: {},
                    Q15: {},
                    Q16: {},
                    signatures: {}
                },
                documents: [],
                document: null,
            },
            modifiedOn: new Date().getTime(),
        }

        let prefixes: Record<string, string> = {};
        // let prefix: string | undefined | null = undefined;

        let ancillaryBenefit: FAM.IBenefit | null = null;
        let visionBenefit: FAM.IBenefit | null;
        let pharmacyBenefit: FAM.IBenefit | null;


        let individualDeductibles: Record<string, FAM.IFlexibleFieldValue> | null = null;
        let familyDeductibles: Record<string, FAM.IFlexibleFieldValue> | null = null;
        let individualGlobalOOPs: Record<string, FAM.IFlexibleFieldValue> | null = null;
        let familyGlobalOOPs: Record<string, FAM.IFlexibleFieldValue> | null = null;
        let individualStandardOOPs: Record<string, FAM.IFlexibleFieldValue> | null = null;
        let familyStandardOOPs: Record<string, FAM.IFlexibleFieldValue> | null = null;

        let dentalBenefit: FAM.IBenefit | null = null;


        // Setup 
        // ------------------------------------------------------------------------------------------
        // Medical Category
        // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_category&id=42ad9a7f-d63c-ec11-b6e5-0022480a9561
        const categoryMedical = state.data.config.cache.map_bcbs_config_category_named.Medical;

        // Ancillary Category
        const categoryAncillary = state.data.config.cache.map_bcbs_config_category_named.Ancillary;

        // Dental Category
        const categoryDental = state.data.config.cache.map_bcbs_config_category_named.Dental;

        // Telehealth Category
        const categoryTelehealth = state.data.config.cache.map_bcbs_config_category_named.Telehealth as IXrmTypes.bcbs_config_category | null;

        // Vision Category
        const categoryVision = state.data.config.cache.map_bcbs_config_category_named.Vision;

        // Vision Category
        const categoryPharmacy = state.data.config.cache.map_bcbs_config_category_named.Pharmacy;


        // Group Configuration
        // ------------------------------------------------------------------------------------------
        state.data.json.fields.forEach(eachField => {

            const configAttribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];
            const masterAttribute = configAttribute
                ? state.data.config.cache.map_bcbs_config_masterattribute[configAttribute._bcbs_config_masterattributeid_value!]
                : null;

            // Send Initial Cards To
            // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=862efbb7-be5d-ec11-8f8f-000d3a5a0d43
            // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=24a46909-d05d-ec11-8f8f-000d3a5a062b
            if (masterAttribute?.bcbs_config_masterattributeid === '862efbb7-be5d-ec11-8f8f-000d3a5a0d43') {
                ret.blob.data.bcbs_sendinitialcardsto = eachField.values[0].value?.type === 'entity'
                    ? eachField.values[0].value.name
                    : undefined;
            }

            // After Set-up Send Cards To
            // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=7e2efbb7-be5d-ec11-8f8f-000d3a5a0d43
            // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=1ca46909-d05d-ec11-8f8f-000d3a5a062b
            if (masterAttribute?.bcbs_config_masterattributeid === '7e2efbb7-be5d-ec11-8f8f-000d3a5a0d43') {
                ret.blob.data.bcbs_aftersetupsendcardsto = eachField.values[0].value?.type === 'entity'
                    ? eachField.values[0].value.name
                    : undefined;
            }

            // Service Center Phone
            // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=6a2efbb7-be5d-ec11-8f8f-000d3a5a0d43
            // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=08a46909-d05d-ec11-8f8f-000d3a5a062b
            if (masterAttribute?.bcbs_config_masterattributeid === '6a2efbb7-be5d-ec11-8f8f-000d3a5a0d43') {
                ret.blob.data.bcbs_servicecenterphone = eachField.values[0].value?.type === 'string'
                    ? eachField.values[0].value.value
                    : undefined;
            }


            // Group Logo 
            // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=ddd9562e-7609-ee11-8f6e-000d3a305861
            // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=c4fe0f95-7609-ee11-8f6e-000d3a305861
            if (masterAttribute?.bcbs_config_masterattributeid === 'ddd9562e-7609-ee11-8f6e-000d3a305861') {
                ret.blob.data.bcbs_isgrouplogo = eachField.values[0].value?.type === 'bool'
                    ? eachField.values[0].value.value
                    : undefined;
            }

            // Display Plan Name
            // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=c1c3fbfc-7709-ee11-8f6e-000d3a305861
            // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=1881c830-7809-ee11-8f6e-000d3a305861

            if (masterAttribute?.bcbs_config_masterattributeid === 'c1c3fbfc-7709-ee11-8f6e-000d3a305861') {
                ret.blob.data.bcbs_planname = eachField.values[0].value?.type === 'bool'
                    ? eachField.values[0].value.value
                    : undefined;
            }



            // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/453846

            // Service Center (claims processing) 
            // Bcbs_grpconfigServiceCenterclaimsprocessing
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=bca36909-d05d-ec11-8f8f-000d3a5a062b
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=202efbb7-be5d-ec11-8f8f-000d3a5a0d43
            if (masterAttribute?.bcbs_config_masterattributeid === '202efbb7-be5d-ec11-8f8f-000d3a5a0d43') {
                ret.blob.data.bcbs_grpconfigservicecenterclaimsprocessing = eachField.values[0].value?.type === 'entity'
                    ? eachField.values[0].value.name
                    : undefined;

            }

        });

        // Plans
        // ------------------------------------------------------------------------------------------
        state.data.json.plans.forEach(eachPlan => {

            // find the right plan
            if (eachPlan.id !== checkList.bcbs_emchecklist.bcbs_planid)
                return;

            eachPlan.fields.forEach(eachField => {

                const configAttribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];
                const masterAttribute = configAttribute
                    ? state.data.config.cache.map_bcbs_config_masterattribute[configAttribute._bcbs_config_masterattributeid_value!]
                    : null;

                if (!masterAttribute)
                    return;

                // Quantum Provider Number
                // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=2fe8c92a-fb0a-ee11-8f6e-00224808e3f5
                // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=ad6df562-fb0a-ee11-8f6e-00224808e3f5
                if (masterAttribute.bcbs_config_masterattributeid === '2fe8c92a-fb0a-ee11-8f6e-00224808e3f5') {
                    ret.blob.data.bcbs_customdisplayname = eachField.values[0].value?.type === 'string'
                        ? eachField.values[0].value.value
                        : undefined;
                }

            });
        });

        // Benefits
        // ------------------------------------------------------------------------------------------
        state.data.json.benefits.forEach(eachBenefit => {

            // TODO REWRITE THIS TO USE FIELDS TO LOOKUP

            // cache list of fields upon first call
            const fieldInfosMemoized = Memoize(() => {
                return FieldsToLookupByMasterAttributeId(state.data.config, eachBenefit.fields);
            });

            // skip if not a benefit for current plan
            if (!plan?.benefitRefs.some(t => t.id === eachBenefit.id))
                return;

            // Benefits - Medical
            // ------------------------------------------------------------------------------------------
            if (eachBenefit.bcbs_config_categoryid === categoryMedical.bcbs_config_categoryid) {

                ret.blob.data.medicalBenefit = { id: eachBenefit.id, name: eachBenefit.name };
                ret.blob.data.medicalBenefitTiers = eachBenefit.tiers;

                eachBenefit.fields.forEach(eachField => {

                    const configAttribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];
                    const masterAttribute = configAttribute
                        ? state.data.config.cache.map_bcbs_config_masterattribute[configAttribute._bcbs_config_masterattributeid_value!]
                        : null;

                    if (!masterAttribute)
                        return;

                    // Med Products
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=3f91d4e1-c24b-ec11-8f8e-000d3a3340f2
                    if (masterAttribute.bcbs_config_masterattributeid === '3f91d4e1-c24b-ec11-8f8e-000d3a3340f2') {
                        ret.blob.data.bcbs_productsid = eachField.values[0].value?.type === 'entity'
                            ? eachField.values[0].value.name
                            : undefined;
                    }

                    // Med Drug: Drugs are covered under
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=d991d4e1-c24b-ec11-8f8e-000d3a3340f2
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=2289cb95-ce4b-ec11-8f8e-000d3a3349fe
                    if (masterAttribute.bcbs_config_masterattributeid === 'd991d4e1-c24b-ec11-8f8e-000d3a3340f2') {
                        ret.blob.data.bcbs_epo_drgugsarecoveredunder = eachField.values[0].value?.type === 'entity'
                            ? eachField.values[0].value.name
                            : undefined;
                    }

                    // Med Dental: If "Yes", under
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=b591d4e1-c24b-ec11-8f8e-000d3a3340f2
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=fa88cb95-ce4b-ec11-8f8e-000d3a3349fe
                    if (masterAttribute?.bcbs_config_masterattributeid === 'b591d4e1-c24b-ec11-8f8e-000d3a3340f2') {
                        ret.blob.data.bcbs_epo_ifyesunder = eachField.values[0].value?.type === 'entity'
                            ? eachField.values[0].value.name
                            : undefined;
                    }

                    // Prev Care: Mammography Network Provider
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=f9a1a560-3563-ec11-8f8f-002248040f87
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=bfe276fc-ee63-ec11-8f8f-00224804002c
                    if (masterAttribute?.bcbs_config_masterattributeid === 'f9a1a560-3563-ec11-8f8f-002248040f87') {
                        ret.blob.data.bcbs_mammographynetworkprovider = eachField.values[0].value?.type === 'bool'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                    // Others: Is pre-auth required for Inpat fac
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=32a86ae4-ed73-ec11-8941-000d3a352e43
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=5e86f6d4-ef73-ec11-8941-000d3a352986
                    if (masterAttribute?.bcbs_config_masterattributeid === '32a86ae4-ed73-ec11-8941-000d3a352e43') {
                        ret.blob.data.bcbs_ispreauthorizationrequiredforinpatientfac = eachField.values[0].value?.type === 'bool'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                    // Others - Is pre-auth required for Outpat fac
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=6c86f6d4-ef73-ec11-8941-000d3a352986
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=40a86ae4-ed73-ec11-8941-000d3a352e43
                    if (masterAttribute?.bcbs_config_masterattributeid === '40a86ae4-ed73-ec11-8941-000d3a352e43') {
                        ret.blob.data.bcbs_ispreauthorizationrequiredforoutpatientfa = eachField.values[0].value?.type === 'bool'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                    // MHSU: Does CBA manage Pre-Auth
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=0052723e-f777-ec11-8d21-000d3a5b9199
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=680316f6-7378-ec11-8d21-000d3a34c462
                    if (masterAttribute?.bcbs_config_masterattributeid === '0052723e-f777-ec11-8d21-000d3a5b9199') {
                        ret.blob.data.bcbs_doescompanionbenefitalternativecbamanagep = eachField.values[0].value?.type === 'bool'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                    // RV: Is routine vision covered?
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=fe91d4e1-c24b-ec11-8f8e-000d3a3340f2
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=4889cb95-ce4b-ec11-8f8e-000d3a3349fe
                    if (masterAttribute.bcbs_config_masterattributeid === 'fe91d4e1-c24b-ec11-8f8e-000d3a3340f2') {
                        ret.blob.data.bcbs_epo_rountinevisioncovered = eachField.values[0].value?.type === 'entity'
                            ? eachField.values[0].value.name
                            : undefined;
                    }


                    // Telehealth
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=e8366214-9a62-ec11-8f8f-002248040f87
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=55621da4-9d62-ec11-8f8f-002248040a61
                    if (masterAttribute.bcbs_config_masterattributeid === 'e8366214-9a62-ec11-8f8f-002248040f87') {
                        ret.blob.data.bcbs_telehealthid = eachField.values[0].value?.type === 'entity'
                            ? eachField.values[0].value.name
                            : undefined;
                    }


                    // Per Member Deductibles
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=5791d4e1-c24b-ec11-8f8e-000d3a3340f2
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=9e88cb95-ce4b-ec11-8f8e-000d3a3349fe
                    if (masterAttribute.bcbs_config_masterattributeid === '5791d4e1-c24b-ec11-8f8e-000d3a3340f2') {
                        individualDeductibles = ArrayToLookupByPredicate(eachField.values, (item) => item.tierRef?.id ?? '?');
                    }


                    // Per Family Deductibles
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=5991d4e1-c24b-ec11-8f8e-000d3a3340f2
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=a088cb95-ce4b-ec11-8f8e-000d3a3349fe
                    if (masterAttribute.bcbs_config_masterattributeid === '5991d4e1-c24b-ec11-8f8e-000d3a3340f2') {
                        familyDeductibles = ArrayToLookupByPredicate(eachField.values, (item) => item.tierRef?.id ?? '?');
                    }


                    // Global OOP Per Member
                    // (THERE IS NOT OUT OF NETWORK)
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=6f91d4e1-c24b-ec11-8f8e-000d3a3340f2
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=b288cb95-ce4b-ec11-8f8e-000d3a3349fe
                    if (masterAttribute.bcbs_config_masterattributeid === '6f91d4e1-c24b-ec11-8f8e-000d3a3340f2') {
                        individualGlobalOOPs = ArrayToLookupByPredicate(eachField.values, (item) => item.tierRef?.id ?? '?');
                    }


                    // Global OOP Per Family
                    // (THERE IS NOT OUT OF NETWORK)
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=7191d4e1-c24b-ec11-8f8e-000d3a3340f2
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=b488cb95-ce4b-ec11-8f8e-000d3a3349fe
                    if (masterAttribute.bcbs_config_masterattributeid === '7191d4e1-c24b-ec11-8f8e-000d3a3340f2') {
                        familyGlobalOOPs = ArrayToLookupByPredicate(eachField.values, (item) => item.tierRef?.id ?? '?');
                    }


                    // Standard OOP Per Member
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=7391d4e1-c24b-ec11-8f8e-000d3a3340f2
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=b688cb95-ce4b-ec11-8f8e-000d3a3349fe
                    if (masterAttribute.bcbs_config_masterattributeid === '7391d4e1-c24b-ec11-8f8e-000d3a3340f2') {
                        individualStandardOOPs = ArrayToLookupByPredicate(eachField.values, (item) => item.tierRef?.id ?? '?');
                    }

                    // Standard OOP Per Family
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=7591d4e1-c24b-ec11-8f8e-000d3a3340f2
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=b888cb95-ce4b-ec11-8f8e-000d3a3349fe
                    if (masterAttribute.bcbs_config_masterattributeid === '7591d4e1-c24b-ec11-8f8e-000d3a3340f2') {
                        familyStandardOOPs = ArrayToLookupByPredicate(eachField.values, (item) => item.tierRef?.id ?? '?');
                    }


                    // Rx Pre-Auth - SMBM
                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=103c884e-720f-ee11-8f6d-000d3a305861
                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=5d1b4bb4-720f-ee11-8f6d-000d3a305861
                    if (masterAttribute.bcbs_config_masterattributeid === '103c884e-720f-ee11-8f6d-000d3a305861') {
                        ret.blob.data.bcbs_rxpreauth_smbm = eachField.values[0].value?.type === 'bool'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                });
            }

            // Benefits - Ancillary
            // ------------------------------------------------------------------------------------------
            if (eachBenefit.bcbs_config_categoryid === categoryAncillary.bcbs_config_categoryid) {

                ancillaryBenefit = eachBenefit;
                ret.blob.data.ancillaryBenefit = { id: eachBenefit.id, name: eachBenefit.name };

                eachBenefit.fields.forEach(eachField => {

                    const configAttribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];
                    const masterAttribute = configAttribute
                        ? state.data.config.cache.map_bcbs_config_masterattribute[configAttribute._bcbs_config_masterattributeid_value!]
                        : null;

                    if (!masterAttribute)
                        return;

                    // Rx PreAuth - Specialty Medical Benefit Management        
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=2d98f1ab-3d58-ec11-8f8f-000d3a597a8a
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=c478ae9c-2759-ec11-8f8f-000d3a597a8a
                    if (masterAttribute.bcbs_config_masterattributeid === '2d98f1ab-3d58-ec11-8f8f-000d3a597a8a') {
                        ret.blob.data.bcbs_novologix = eachField.values[0].value?.type === 'bool'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                    // NIA Program(s)
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=2598f1ab-3d58-ec11-8f8f-000d3a597a8a
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=bc78ae9c-2759-ec11-8f8f-000d3a597a8a
                    if (masterAttribute.bcbs_config_masterattributeid === '2598f1ab-3d58-ec11-8f8f-000d3a597a8a') {
                        const field = (eachField.visibility ?? 0 == 0) && eachField.values[0].value?.type === 'entity'
                            ? eachField.values[0].value
                            : null;

                        if (field) {
                            ret.blob.data.bcbs_nia_programs = field.name
                            ret.blob.data.bcbs_nia_programs_split = field?.name
                                ? field?.name.split(/\band\b|,/g).map(q => q.trim())
                                : []
                        }

                    }


                    // NIA Program(s)
                    // Multi Select Option Version
                    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=0803c40e-1754-ef11-a317-000d3a17460e
                    if (masterAttribute.bcbs_config_masterattributeid === '0803c40e-1754-ef11-a317-000d3a17460e') {

                        const field = (eachField.visibility ?? 0 == 0) && eachField.values[0].value?.type === 'multioptionset'
                            ? eachField.values[0].value
                            : null;

                        if (field) {
                            ret.blob.data.bcbs_nia_programs = field.value;
                            ret.blob.data.bcbs_nia_programs_split = field.options.map(t => t.name);
                        }
                    }



                    // Essential Advocate
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=0598f1ab-3d58-ec11-8f8f-000d3a597a8a
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=ee60884d-3ef2-ec11-bb3d-000d3a32524c
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=9678ae9c-2759-ec11-8f8f-000d3a597a8a
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=da75be0f-ee5a-ed11-9561-00224808116e
                    if (masterAttribute.bcbs_config_masterattributeid === '0598f1ab-3d58-ec11-8f8f-000d3a597a8a') {
                        ret.blob.data.bcbs_essentialadvocate = ret.blob.data.bcbs_essentialadvocate ?? (
                            eachField.values[0].value?.type === 'bool'
                                ? eachField.values[0].value.value
                                : undefined
                        )
                    }

                    // External Services Vendor
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=fb97f1ab-3d58-ec11-8f8f-000d3a597a8a
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&pagetype=entityrecord&etn=bcbs_config_attribute&id=8c78ae9c-2759-ec11-8f8f-000d3a597a8a
                    if (masterAttribute.bcbs_config_masterattributeid === 'fb97f1ab-3d58-ec11-8f8f-000d3a597a8a') {
                        ret.blob.data.bcbs_externalservicesvendorid = eachField.values[0].value?.type === 'entity'
                            ? eachField.values[0].value.name
                            : undefined;
                    }

                    // Quantum URL
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=5b559bfc-fe0a-ee11-8f6e-00224808e3f5
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=5ca42327-ff0a-ee11-8f6e-00224808e3f5
                    if (masterAttribute.bcbs_config_masterattributeid === '5b559bfc-fe0a-ee11-8f6e-00224808e3f5') {
                        ret.blob.data.bcbs_quantum_url = eachField.values[0].value?.type === 'string'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                    // Quantum Provider Number
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=7178aba7-fe0a-ee11-8f6e-00224808e3f5
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=3ab4fec5-fe0a-ee11-8f6e-00224808e3f5
                    if (masterAttribute.bcbs_config_masterattributeid === '7178aba7-fe0a-ee11-8f6e-00224808e3f5') {
                        ret.blob.data.bcbs_quantum_providernumber = eachField.values[0].value?.type === 'string'
                            ? eachField.values[0].value.value
                            : undefined;
                    }


                    // Accolade URL
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=57256e5a-fe0a-ee11-8f6e-00224808e3f5
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=da515c87-fe0a-ee11-8f6e-00224808e3f5
                    if (masterAttribute.bcbs_config_masterattributeid === '57256e5a-fe0a-ee11-8f6e-00224808e3f5') {
                        ret.blob.data.bcbs_accolade_url = eachField.values[0].value?.type === 'string'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                    // Accolade Provider Number
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=109f71d5-fd0a-ee11-8f6e-00224808e3f5
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=30000e3c-fe0a-ee11-8f6e-00224808e3f5
                    if (masterAttribute.bcbs_config_masterattributeid === '109f71d5-fd0a-ee11-8f6e-00224808e3f5') {
                        ret.blob.data.bcbs_acccolade_providernumber = eachField.values[0].value?.type === 'string'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                    // Quantum Customer Service Number
                    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=cd68c10c-dd50-ee11-be6f-00224808116e
                    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=88f893b2-dc50-ee11-be6f-00224808116e
                    if (masterAttribute.bcbs_config_masterattributeid === '88f893b2-dc50-ee11-be6f-00224808116e') {
                        ret.blob.data.bcbs_quantum_csnumber = eachField.values[0].value?.type === 'string'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                    // Accolade Customer Service Number
                    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=de14bdaa-da50-ee11-be6f-00224808116e
                    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=eca72d1e-da50-ee11-be6f-00224808116e 
                    if (masterAttribute.bcbs_config_masterattributeid === 'eca72d1e-da50-ee11-be6f-00224808116e') {
                        ret.blob.data.bcbs_accolade_csnumber = eachField.values[0].value?.type === 'string'
                            ? eachField.values[0].value.value
                            : undefined;
                    }


                    // HealthCheck360 Provider Number
                    if (masterAttribute.bcbs_config_masterattributeid === 'd8c60c5d-a47d-ee11-8179-00224808116e') {
                        ret.blob.data.bcbs_healthcheck360_providernumber = eachField.values[0].value?.type === 'string'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                    // HealthCheck360 URL
                    if (masterAttribute.bcbs_config_masterattributeid === 'fc2a7d12-a57d-ee11-8179-000d3a305861') {
                        ret.blob.data.bcbs_healthcheck360_url = eachField.values[0].value?.type === 'string'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                    // HealthCheck360 Customer Service Number
                    if (masterAttribute.bcbs_config_masterattributeid === 'f8171e7b-a57d-ee11-8179-000d3a305861') {
                        ret.blob.data.bcbs_healthcheck360_csnumber = eachField.values[0].value?.type === 'string'
                            ? eachField.values[0].value.value
                            : undefined;
                    }


                    // HealthCheck360 Provider Number
                    if (masterAttribute.bcbs_config_masterattributeid === '550c23de-a57d-ee11-8179-000d3a305861') {
                        ret.blob.data.bcbs_includedhealth_providernumber = eachField.values[0].value?.type === 'string'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                    // HealthCheck360 URL
                    if (masterAttribute.bcbs_config_masterattributeid === '2f9d7a5a-a67d-ee11-8179-000d3a305861') {
                        ret.blob.data.bcbs_includedhealth_url = eachField.values[0].value?.type === 'string'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                    // HealthCheck360 Customer Service Number
                    if (masterAttribute.bcbs_config_masterattributeid === 'd70be915-a77d-ee11-8179-000d3a305861') {
                        ret.blob.data.bcbs_includedhealth_csnumber = eachField.values[0].value?.type === 'string'
                            ? eachField.values[0].value.value
                            : undefined;
                    }


                    // 24-Hour Nurse Advisor
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=fd97f1ab-3d58-ec11-8f8f-000d3a597a8a
                    // https://bcbsonlinedev.crm.dynamics.com/main.aspx?appid=1d3e2423-40b4-46b0-962f-d436676c9415&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=8e78ae9c-2759-ec11-8f8f-000d3a597a8a
                    if (masterAttribute.bcbs_config_masterattributeid === 'fd97f1ab-3d58-ec11-8f8f-000d3a597a8a') {
                        ret.blob.data.bcbs_ancben24hournurseadvisor = eachField.values[0].value?.type === 'bool'
                            ? eachField.values[0].value.value
                            : undefined;
                    }

                });
            }

            // Benefits - Dental
            // ------------------------------------------------------------------------------------------
            if (eachBenefit.bcbs_config_categoryid === categoryDental.bcbs_config_categoryid) {

                dentalBenefit = eachBenefit;

                ret.blob.data.dentalBenefit = { id: eachBenefit.id, name: eachBenefit.name, };

                eachBenefit.fields.forEach(eachField => {

                    const configAttribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];
                    const masterAttribute = configAttribute
                        ? state.data.config.cache.map_bcbs_config_masterattribute[configAttribute._bcbs_config_masterattributeid_value!]
                        : null;

                    if (!masterAttribute)
                        return;


                    // Dental Plan Type
                    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&newWindow=true&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=e404cfb5-a332-ec11-b6e5-0022480a5289
                    if (masterAttribute.bcbs_config_masterattributeid === 'e404cfb5-a332-ec11-b6e5-0022480a5289') {
                        ret.blob.data.bcbs_dentalplantypeid = eachField.values[0].value?.type === 'entity'
                            ? eachField.values[0].value.name
                            : undefined;
                    }

                });
            }

            // Benefits - Telehealth
            // ------------------------------------------------------------------------------------------
            if (eachBenefit.bcbs_config_categoryid === categoryTelehealth?.bcbs_config_categoryid) {
                ret.blob.data.telehealthBenefit = { id: eachBenefit.id, name: eachBenefit.name, };
            }

            // Benefits - Pharmacy
            // ------------------------------------------------------------------------------------------
            if (eachBenefit.bcbs_config_categoryid === categoryPharmacy.bcbs_config_categoryid) {
                pharmacyBenefit = eachBenefit;

                ret.blob.data.pharmacyBenefit = { id: eachBenefit.id, name: eachBenefit.name, };

                eachBenefit.fields.forEach(eachField => {

                    const configAttribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];
                    const masterAttribute = configAttribute
                        ? state.data.config.cache.map_bcbs_config_masterattribute[configAttribute._bcbs_config_masterattributeid_value!]
                        : null;

                    if (!masterAttribute)
                        return;

                    // PBM
                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=f03c33c5-b861-ec11-8f8f-002248040f87
                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=d8b83002-7160-ec11-8f8f-000d3a5a07fc
                    if (masterAttribute.bcbs_config_masterattributeid === 'd8b83002-7160-ec11-8f8f-000d3a5a07fc') {
                        ret.blob.data.bcbs_pbm = eachField.values[0].value?.type === 'entity'
                            ? eachField.values[0].value.name
                            : undefined;
                    }
                });

            }

            // Benefits - Vision
            // ------------------------------------------------------------------------------------------
            if (eachBenefit.bcbs_config_categoryid === categoryVision.bcbs_config_categoryid) {
                visionBenefit = eachBenefit;

                ret.blob.data.visionBenefit = { id: eachBenefit.id, name: eachBenefit.name };

                eachBenefit.fields.forEach(eachField => {

                    const configAttribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];
                    const masterAttribute = configAttribute
                        ? state.data.config.cache.map_bcbs_config_masterattribute[configAttribute._bcbs_config_masterattributeid_value!]
                        : null;

                    if (!masterAttribute)
                        return;

                    // Vision Product Type
                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=1ab64a16-a640-ec11-8c60-000d3a5b8789
                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=223bcd68-a040-ec11-8c60-000d3a5b805c
                    if (masterAttribute.bcbs_config_masterattributeid === '223bcd68-a040-ec11-8c60-000d3a5b805c') {
                        ret.blob.data.bcbs_visionproducttype = eachField.values[0].value?.type === 'entity'
                            ? eachField.values[0].value.name
                            : undefined;
                    }
                });
            }

        });


        const toCurrency = (fieldValues: Record<string, FAM.IFlexibleFieldValue> | null, tier?: FAM.ITierLevel) => {
            if (!fieldValues)
                return null;

            const field = fieldValues[tier?.id ?? '?'];

            return field?.value?.type === 'number'
                ? field?.value?.value
                : null;
        }

        const deductibles: IDeductible[] = [];

        ret.blob.data.medicalBenefitTiers?.forEach(eachTier => {
            const individualDeductible = toCurrency(individualDeductibles, eachTier);
            const familyDeductible = toCurrency(familyDeductibles, eachTier);
            const individualGlobalOOP = toCurrency(individualGlobalOOPs, eachTier);
            const familyGlobalOOP = toCurrency(familyGlobalOOPs, eachTier);
            const individualStandardOOP = toCurrency(individualStandardOOPs, eachTier);
            const familyStandardOOP = toCurrency(familyStandardOOPs, eachTier);

            const deductible: IDeductible = {
                name: eachTier.name,
                individualDeductible,
                familyDeductible,
                individualGlobalOOP,
                familyGlobalOOP,
                individualStandardOOP,
                familyStandardOOP,
            };

            deductibles.push(deductible);
        });

        ret.blob.data.deductibles = deductibles;

        // Group Numbers
        // ------------------------------------------------------------------------------------------
        state.data.json.groupStructure.groupNumbers.forEach(eachGroupNumber => {

            if (eachGroupNumber.deactivated)
                return;

            if (eachGroupNumber?.planRef?.id !== checkList.bcbs_emchecklist.bcbs_planid)
                return;

            if (
                eachGroupNumber.planRef &&
                eachGroupNumber.prefix
            ) {
                prefixes[eachGroupNumber.prefix] = eachGroupNumber.prefix;
                ret.blob.data.prefix = eachGroupNumber.prefix;
            }

            let network = '';
            let alpha_prefix = '';
            if (!eachGroupNumber.deactivated)
                eachGroupNumber.fields.forEach(eachField => {
                    // Network
                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=e87c1bc6-d18d-ec11-b400-0022480a93ed
                    if (eachField.configRef.id === 'e87c1bc6-d18d-ec11-b400-0022480a93ed') {
                        network = eachField.values[0].value?.type === 'entity'
                            ? eachField.values[0].value.name
                            : '';
                    }

                    // Alpha Prefix
                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=d07c1bc6-d18d-ec11-b400-0022480a93ed
                    if (eachField.configRef.id === 'd07c1bc6-d18d-ec11-b400-0022480a93ed') {
                        alpha_prefix = eachField.values[0].value?.type === 'string'
                            ? eachField.values[0].value.value
                            : 'N/A';
                    }

                });

            const network_alphaprefix = `${network ? network : 'No Alt Network'} - ${alpha_prefix}`;

            if (!ret.blob.data.network_alphaprefixes)
                ret.blob.data.network_alphaprefixes = [];

            if (!ret.blob.data.network_alphaprefixes.includes(network_alphaprefix))
                ret.blob.data.network_alphaprefixes.push(network_alphaprefix);

        });

        ret.blob.input.Q5.yesNo = ret.blob.data.bcbs_isgrouplogo;

        ret.blob.input.Q11.bcbs_planname = ret.blob.data.bcbs_planname
        if (ret.blob.input.Q11.bcbs_planname)
            ret.blob.input.Q11.planName = ret.blob.data.plan.name;

        ret.blob.input.Q16.hasTeladoc = !!ret.blob.data.telehealthBenefit


        // // if current user is not found as a reviewer, add
        // if (!ret.blob.input.reviewers.find(q => q.id === systemUser.systemuserid))
        //     ret.blob.input.reviewers.push({
        //         type: 'entity',
        //         entityType: 'systemuser',
        //         id: systemUser.systemuserid!,
        //         name: systemUser.fullname!,
        //     });

        setChecklist(ret);
    }

    // store loading
    useEffect(() => {
        (async () => {
            try {
                if (checklist)
                    return;

                setChecklistLoading(true);

                if (!state?.data?.snapshot?.bcbs_groupconfigurationsnapshotid)
                    return;

                if (!state?.data?.json)
                    return;

                if (!state.data.checkList) {
                    dispatch(loadCheckListAction(appDataSource));
                    return;
                }

                const checkList = await (async () => {
                    const bcbs_emchecklist = await appDataSource.get_bcbs_emchecklist(bcbs_emchecklistid!);
                    const checklist: ICheckList = {
                        bcbs_emchecklist: bcbs_emchecklist,
                        blob: null as any,
                        blobEtag: null,
                        versionNumber: bcbs_emchecklist.versionnumber,

                    }

                    return checklist;
                })();

                if (!checkList)
                    return;

                if (checkList?.bcbs_emchecklist && checkList?.bcbs_emchecklist.bcbs_blobversion) {
                    const loadResult = await appDataSource.get_bcbs_emchecklist_blob(checkList.bcbs_emchecklist);
                    const ret: ICheckList = {
                        ...checkList,
                        blob: loadResult.blob as ICheckListData,
                        modifiedOn: new Date().getTime(),
                    }

                    // // if current user is not found as a reviewer, add
                    // if (!ret.blob.input.reviewers.find(q => q.id === systemUser.systemuserid))
                    //     ret.blob.input.reviewers.push({
                    //         type: 'entity',
                    //         entityType: 'systemuser',
                    //         id: systemUser.systemuserid!,
                    //         name: systemUser.fullname!,
                    //     });

                    setChecklist(ret);
                    return;
                } else {
                    await rebuildChecklist();
                }

            }
            catch (error) {

                debugger;
                // lets create a new one is payload is not providing one
                emitCustomEvent({
                    eventType: 'globalException',
                    details: error
                });
            }
            finally {
                setChecklistLoading(false);
                setCheckpointOn(new Date().getTime());
            }

        })();
    }, [state?.data?.json, state?.data?.checkList,]);

    // document loading
    useEffect(() => {
        (async () => {
            if (!checklist)
                return;

            if (!documentFile && checklist.blob && checklist.blob.documents && checklist.blob.documents.length > 0) {
                try {
                    setPdfLoading(true);
                    let pdfDataBlob: Blob;
                    if (checklist.blob.documents.length > 0) {
                        const importPDFLibPromise = import('pdf-lib');
                        const blobsPromise = checklist.blob.documents.map(document => appDataSource.blob.downloadBlob(document.blobName, undefined, "generated"));

                        // if only a single PDF, load without merge
                        if (checklist.blob.documents.length === 1) {
                            const file = await blobsPromise[0];
                            pdfDataBlob = file.blob;
                        } else {
                            // merging multiple pdfs
                            const importPDFLib = await importPDFLibPromise;
                            const pdfDoc = await importPDFLib.PDFDocument.create();

                            for (var i = 0; i < blobsPromise.length; i++) {
                                const file = (await blobsPromise[i]);
                                const donorPdfBytes = await file.blob.arrayBuffer();
                                const donorPdfDoc = await importPDFLib.PDFDocument.load(donorPdfBytes);
                                const docLength = donorPdfDoc.getPageCount();
                                for (var k = 0; k < docLength; k++) {
                                    const [donorPage] = await pdfDoc.copyPages(donorPdfDoc, [k]);
                                    pdfDoc.addPage(donorPage);
                                }
                            }
                            let pdfDataUri: Uint8Array;
                            pdfDataUri = await pdfDoc.save();
                            pdfDataBlob = new Blob([pdfDataUri], { type: "application/pdf" });
                        }

                        const pdfDataObjectUrl = URL.createObjectURL(pdfDataBlob);

                        setDocumentFile(pdfDataObjectUrl);
                    }


                } finally {
                    setPdfLoading(false);
                }
            }

        })();


    }, [checklist?.blob]);

    // pdf viewer/detach handler
    useEffect(() => {
        if (documentFile) {
            if (detachedViewer) {
                const src = documentFile
                    ? `./generic/web/viewer.html?file=${encodeURIComponent(documentFile)}`
                    : `./generic/web/viewer.html`

                if (!refNewWindow.current) {

                    const newWindow = window.open(src,
                        'popUpWindow',
                        'height=600,width=800,left=100,top=100,resizable=yes,scrollbars=yes,toolbar=yes,menubar=no,location=no,directories=no, status=yes'
                    );

                    refNewWindow.current = newWindow;
                    if (newWindow) {
                        newWindow.onload = function () {
                            newWindow.onunload = function () {
                                setDetachedViewer(false);
                            }
                            // TODO save handler
                        }

                    }
                }
                else {
                    refNewWindow.current.location = src;
                }

            } else {
                if (refNewWindow.current) {
                    refNewWindow.current.close();
                    refNewWindow.current = null;
                }
            }
        }
    }, [documentFile, detachedViewer])

    // Save 
    const Save = async (saveInstructions: ISaveTransaction) => {

        let newSaveModel: ISaveTransaction | null = { ...saveInstructions };
        debugger
        // choosing items to carry from previous transaction
        if (saveTransaction?.validation?.state === EValidationState.Rejected) {

            newSaveModel.bcbs_emchecklist_updates = {
                ...newSaveModel.bcbs_emchecklist_updates,
            };

            if (saveTransaction.bcbs_emchecklist_updates?.bcbs_primarynotes)
                newSaveModel.bcbs_emchecklist_updates.bcbs_primarynotes = saveTransaction.bcbs_emchecklist_updates?.bcbs_primarynotes;

            if (saveTransaction.bcbs_emchecklist_updates?.bcbs_marketingnotes)
                newSaveModel.bcbs_emchecklist_updates.bcbs_marketingnotes = saveTransaction.bcbs_emchecklist_updates?.bcbs_marketingnotes;

            if (saveTransaction.bcbs_emchecklist_updates?.bcbs_secondarynotes)
                newSaveModel.bcbs_emchecklist_updates.bcbs_secondarynotes = saveTransaction.bcbs_emchecklist_updates?.bcbs_secondarynotes;
        }


        try {
            dispatch(setProcessing(true));

            if (newSaveModel.pendingComment)
                return;

            // create validation if it does not already exists
            if (!newSaveModel.validation) {
                newSaveModel.validation = {
                    state: EValidationState.Validating,
                    validationMessages: [],
                    data: {}
                }

                if (newSaveModel.submit) {
                    const questions: (keyof IQResponses)[] = [
                        "Q1", "Q2", "Q3", "Q4",
                        "Q5", "Q6", "Q7", "Q8",
                        "Q9", "Q10", "Q11", "Q12",
                        "Q13", "Q14", "Q15", "Q16"
                    ];

                    if (newSaveModel.checklist?.blob.input)
                        for (const t of Object.entries(newSaveModel.checklist.blob.input)) {
                            const [key, value] = t as [keyof IQResponses, IQResponse];

                            if (!questions.includes(key))
                                continue;

                            if (isPrimaryReview || isPendingIssues)
                                if (!value.initial1) {
                                    newSaveModel.validation.validationMessages.push({
                                        title: `Initial 1 is missing on question ${key}.`,
                                        type: EValidationSeverity.warn,
                                    })
                                }

                            if (isSecondaryReview)
                                if (!value.initial2) {
                                    newSaveModel.validation.validationMessages.push({
                                        title: `Initial 2 is missing on question ${key}.`,
                                        type: EValidationSeverity.warn,
                                    })
                                }

                            if (isMarketingReview)
                                if (!value.initial3) {
                                    newSaveModel.validation.validationMessages.push({
                                        title: `Initial 3 is missing on question ${key}.`,
                                        type: EValidationSeverity.warn,
                                    })
                                }
                        }
                }

                if (newSaveModel.validation.validationMessages.length > 0) {
                    newSaveModel.validation.state = EValidationState.PendingUser;
                    return;
                }
                else {
                    newSaveModel.validation.state = EValidationState.Fulfilled;
                }
            }

            if (newSaveModel.validation)
                if (newSaveModel.validation.state === EValidationState.Rejected)
                    return;

            if (newSaveModel.validation)
                if (newSaveModel.validation.state !== EValidationState.Fulfilled)
                    return;

            const saveResult = await appDataSource.save_Checklist_blob(
                newSaveModel.checklist!,
                newSaveModel.bcbs_emchecklist_updates
            );

            setChecklist(saveResult);
            setCheckpointOn(new Date().getTime());

            newSaveModel = null;

        } catch (error) {
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });
        } finally {
            dispatch(setProcessing(false));
            setSaveTransaction(newSaveModel);
        }
    }


    const isPrimaryReview = checklist?.bcbs_emchecklist?.statuscode === IXrmOptions.bcbs_emchecklist.statuscode["Primary Review(1)"].value;
    const isSecondaryReview = checklist?.bcbs_emchecklist?.statuscode === IXrmOptions.bcbs_emchecklist.statuscode["Secondary Review(2)"].value;
    const isMarketingReview = checklist?.bcbs_emchecklist?.statuscode === IXrmOptions.bcbs_emchecklist.statuscode["Marketing Review(3)"].value;
    const isPendingIssues = checklist?.bcbs_emchecklist?.statuscode === IXrmOptions.bcbs_emchecklist.statuscode["Pending Issues(6)"].value;

    const canSave = isPrimaryReview || isSecondaryReview || isMarketingReview || isPendingIssues || state.status.debug.admin;
    const canEditFields = isPrimaryReview || isPendingIssues || state.status.debug.admin;

    const contextMenu = (() => {

        const menu: ICommandBarItemProps[] = [];

        const subMenuSave: IContextualMenuItem[] = [];

        subMenuSave.push({
            key: '_save',
            text: 'Save',
            title: 'Save',
            iconProps: { iconName: 'Save' },
            onClick: (ev, item) => {
                if (checklist)
                    Save({
                        checklist: checklist,
                        submit: false,
                    });
            }
        });

        if (isPrimaryReview || isPendingIssues)
            subMenuSave.push({
                key: 'request_second_review',
                text: 'Request Second Review',
                title: 'Request Second Review',
                disabled: !checklist?.blob.input.signatures.adminCoordinator,
                iconProps: { iconName: 'MedalSolid' },
                onClick: (ev, item) => {
                    if (checklist)
                        Save({
                            checklist: checklist,
                            submit: true,
                            bcbs_emchecklist_updates: {
                                statuscode: IXrmOptions.bcbs_emchecklist.statuscode["Secondary Review(2)"].value,
                            },
                            pendingComment: true,
                        })
                }
            });

        if (isSecondaryReview)
            subMenuSave.push({
                key: 'request_marketing_review',
                text: 'Request Marketing Review',
                title: 'Request Marketing Review',
                disabled: !checklist?.blob.input.signatures.coordinator,
                iconProps: { iconName: 'MedalSolid' },
                onClick: (ev, item) => {
                    if (checklist)
                        Save({
                            checklist: checklist,
                            submit: true,
                            bcbs_emchecklist_updates: {
                                statuscode: IXrmOptions.bcbs_emchecklist.statuscode["Marketing Review(3)"].value,
                                bcbs_sendnotificationcode: IXrmOptions.bcbs_emchecklist.bcbs_sendnotificationcode["Marketing(808900000)"].value,
                                bcbs_ismarketingnotificationsent: false,
                            },
                            pendingComment: true,
                        })
                }
            });

        if (isMarketingReview)
            subMenuSave.push({
                key: 'approve',
                text: 'Approve',
                title: 'Approve',
                disabled: !checklist?.blob.input.signatures.serviceRep,
                iconProps: { iconName: 'MedalSolid' },
                onClick: (ev, item) => {
                    if (checklist)
                        Save({
                            checklist: checklist,
                            submit: true,
                            bcbs_emchecklist_updates: {
                                statuscode: IXrmOptions.bcbs_emchecklist.statuscode["Approved(4)"].value,
                            },
                            pendingComment: true,
                        });
                }
            });

        if (isSecondaryReview || isMarketingReview || state.status.debug.admin)
            subMenuSave.push({
                key: 'return',
                text: 'Return for Corrections',
                title: 'Return for Corrections',
                iconProps: { iconName: 'ReturnToSession' },
                onClick: (ev, item) => {
                    if (checklist)
                        Save({
                            checklist: checklist,
                            submit: true,
                            bcbs_emchecklist_updates: {
                                statuscode: IXrmOptions.bcbs_emchecklist.statuscode["Pending Issues(6)"].value,
                            },
                            pendingComment: true,
                        });
                }
            });

        if (canSave)
            menu.push({
                key: '_save',
                text: 'Save',
                title: 'Save',
                iconProps: { iconName: 'Save' },
                subMenuProps: {
                    items: subMenuSave
                },
            });


        menu.push({
            key: 'documentManagement',
            text: 'Document Management',
            title: 'Document Management',
            iconProps: { iconName: 'DocumentManagement' },
            onClick: (ev, item) => {
                setIsOpenDoc(true);
            }
        });

        // Generate SBC Option
        menu.push({
            key: 'detach viewer',
            text: detachedViewer ? 'Attach Viewer' : 'Detach Viewer',
            title: 'Detach Viewer',
            iconProps: { iconName: 'MiniExpand' },
            disabled: !documentFile,
            onClick: (ev, item) => {
                setDetachedViewer(t => !t);
            }
        });

        // Refresh
        if (state.status.debug.admin) {

            menu.push({
                key: 'force Refresh',
                text: 'Refresh Data',
                title: 'Refresh Data',
                iconProps: { iconName: 'refresh' },
                onClick: (ev, item) => {
                    (async () => {
                        try {
                            dispatch(setProcessing(true));
                            await rebuildChecklist();

                            setRenderIndex(t => t + 1);
                        } catch (error) {

                            // lets create a new one is payload is not providing one
                            emitCustomEvent({
                                eventType: 'globalException',
                                details: error
                            });

                        } finally {
                            dispatch(setProcessing(false));
                        }
                    })();
                }
            });

            menu.push({
                key: 'save as',
                text: 'Download',
                title: 'Download',
                iconProps: { iconName: 'download' },
                onClick: (ev, item) => {
                    const jsonStringify = JSON.stringify(checklist?.blob.data);
                    const blob = new Blob([jsonStringify], { type: 'text/plan;charset=utf-8' });
                    saveAs(blob, `${checklist?.bcbs_emchecklist.bcbs_name}.json`, { autoBom: true });
                }
            });

        }

        return { items: menu, key: 'checklist' };
    })();

    const mapPrefixToLogo = (prefix?: string) => {
        const mapping: Record<string, string> = {
            '70': '70 = BCBS Logo',
            '71': '71 = BCBS Logo',
            '03': '03 = SC Logo',
            '26': '26 = SC Logo',
            '25': '25 = SC Logo',
            '15': '15 = SC Logo',
        };

        return mapping[prefix ?? ''];
    }

    const onRenderInitials = (questionProperty: keyof IQResponses) => {
        if (!checklist)
            return;

        if (!checklist.blob)
            return;

        return <CustomPanel title="Verification" style={{ marginTop: '10px' }}>
            <Stack
                tokens={{ childrenGap: '15px' }}
                styles={{ root: { marginBottom: '10px' } }}
                horizontalAlign="space-around"
                disableShrink
                horizontal
                wrap
            >
                <Stack.Item grow={1}>
                    <CheckBoxWithTextField
                        label='Initial 1'
                        textFieldProps={{
                            value: (checklist.blob.input[questionProperty] as IQResponse).initial1?.name,
                            readOnly: true,
                            disabled: !isPrimaryReview && !isPendingIssues,
                        }}
                        checkboxProps={{
                            checked: !!(checklist.blob.input[questionProperty] as IQResponse).initial1?.id,
                            disabled: !isPrimaryReview && !isPendingIssues,
                            onChange: (event, checked) => {
                                setChecklist(xValue => {
                                    const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                    nValue.blob = { ...nValue.blob! };;
                                    nValue.blob.input = { ...nValue.blob.input };
                                    const nValueInput = nValue.blob.input[questionProperty] as IQResponse;
                                    (nValue.blob.input[questionProperty] as IQResponse) = {
                                        ...nValueInput,
                                        initial1: checked
                                            ? {
                                                type: 'entity',
                                                entityType: 'systemuser',
                                                id: systemUser.systemuserid!,
                                                name: systemUser.fullname!,
                                            }
                                            : undefined,
                                    };
                                    return nValue;
                                });
                            }
                        }}
                    />
                </Stack.Item>

                <Stack.Item grow={1}>
                    <CheckBoxWithTextField
                        label='Initial 2'
                        textFieldProps={{
                            value: (checklist.blob.input[questionProperty] as IQResponse).initial2?.name,
                            disabled: !isSecondaryReview,
                            readOnly: true,
                        }}
                        checkboxProps={{
                            checked: !!(checklist.blob.input[questionProperty] as IQResponse).initial2?.id,
                            disabled: !isSecondaryReview,
                            onChange: (event, checked) => {
                                setChecklist(xValue => {
                                    const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                    nValue.blob = { ...nValue.blob! };;
                                    nValue.blob.input = { ...nValue.blob.input };
                                    const nValueInput = nValue.blob.input[questionProperty] as IQResponse;
                                    (nValue.blob.input[questionProperty] as IQResponse) = {
                                        ...nValueInput,
                                        initial2: checked
                                            ? {
                                                type: 'entity',
                                                entityType: 'systemuser',
                                                id: systemUser.systemuserid!,
                                                name: systemUser.fullname!,
                                            }
                                            : undefined,
                                    };
                                    return nValue;
                                });
                            }
                        }}
                    />
                </Stack.Item>

                <Stack.Item grow={1}>
                    <CheckBoxWithTextField
                        label='Initial 3'
                        textFieldProps={{
                            value: (checklist.blob.input[questionProperty] as IQResponse).initial3?.name,
                            disabled: !isMarketingReview,
                            readOnly: true,
                        }}
                        checkboxProps={{
                            checked: !!(checklist.blob.input[questionProperty] as IQResponse).initial3?.id,
                            disabled: !isMarketingReview,
                            onChange: (event, checked) => {
                                setChecklist(xValue => {
                                    const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                    nValue.blob = { ...nValue.blob! };;
                                    nValue.blob.input = { ...nValue.blob.input };
                                    const nValueInput = nValue.blob.input[questionProperty] as IQResponse;
                                    (nValue.blob.input[questionProperty] as IQResponse) = {
                                        ...nValueInput,
                                        initial3: checked
                                            ? {
                                                type: 'entity',
                                                entityType: 'systemuser',
                                                id: systemUser.systemuserid!,
                                                name: systemUser.fullname!,
                                            }
                                            : undefined,
                                    };
                                    return nValue;
                                });
                            }
                        }}
                    />
                </Stack.Item>
            </Stack>
        </CustomPanel>
    }

    const onRenderCheckListSignature = () => {
        if (!checklist)
            return;

        if (!checklist.blob)
            return;

        return <CustomPanel title="Signatures">
            <Stack tokens={{ childrenGap: '10px' }} wrap disableShrink>

                <Stack.Item grow={1}>
                    <Stack>
                        <Label>Admin/Coordinator</Label>
                        <Stack horizontal>
                            <Stack.Item grow={1}>
                                <TextField
                                    styles={{ root: { width: '100%' } }}
                                    value={checklist.blob.input.signatures.adminCoordinator?.name}
                                    onRenderSuffix={(textFieldProps, textFieldDefaultRender) => {
                                        return <TextField
                                            disabled={!isPrimaryReview && !isPendingIssues}
                                            value={checklist.blob.input.signatures.adminCoordinatorDate?.value
                                                ? formatDate(parseDate(checklist.blob.input.signatures.adminCoordinatorDate?.value, 'yyyy-MM-ddThh:mm:ssZ as local'), 'en-US-ET')
                                                : undefined}
                                            readOnly
                                        />;
                                    }}
                                    disabled={!isPrimaryReview || !isPendingIssues}
                                    onRenderPrefix={(textFieldProps, textFieldDefaultRender) => {
                                        return <Checkbox
                                            checked={!!checklist.blob.input.signatures.adminCoordinator?.id}
                                            disabled={!isPrimaryReview && !isPendingIssues}
                                            onChange={(event, checked) => {
                                                setChecklist(xValue => {
                                                    const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                                    nValue.blob = { ...nValue.blob! };;
                                                    nValue.blob.input = {
                                                        ...nValue.blob.input,
                                                        signatures: {
                                                            ...nValue.blob.input.signatures,
                                                            adminCoordinator: checked
                                                                ? {
                                                                    type: 'entity',
                                                                    entityType: 'systemuser',
                                                                    id: systemUser.systemuserid!,
                                                                    name: systemUser.fullname!,
                                                                }
                                                                : undefined,
                                                            adminCoordinatorDate: checked
                                                                ? {
                                                                    type: 'date',
                                                                    value: formatDate(new Date(), 'local:yyyy-MM-dd')
                                                                }
                                                                : undefined,
                                                        }
                                                    };
                                                    return nValue;
                                                });
                                            }}
                                        />;
                                    }}
                                    readOnly
                                />
                            </Stack.Item>

                        </Stack>
                    </Stack>
                </Stack.Item>

                <Stack.Item grow={1}>
                    <Stack>
                        <Label>Coordinator</Label>
                        <Stack horizontal>
                            <Stack.Item grow={1}>
                                <TextField
                                    styles={{ root: { width: '100%' } }}
                                    disabled={!isSecondaryReview}
                                    value={checklist.blob.input.signatures.coordinator?.name}
                                    onRenderSuffix={(textFieldProps, textFieldDefaultRender) => {
                                        return <TextField
                                            disabled={!isSecondaryReview}
                                            value={checklist.blob.input.signatures.coordinatorDate?.value
                                                ? formatDate(parseDate(checklist.blob.input.signatures.coordinatorDate?.value, 'yyyy-MM-ddThh:mm:ssZ as local'), 'en-US-ET')
                                                : undefined}
                                            readOnly
                                        />;
                                    }}
                                    onRenderPrefix={(textFieldProps, textFieldDefaultRender) => {
                                        return <Checkbox
                                            checked={!!checklist.blob.input.signatures.coordinator?.id}
                                            disabled={!isSecondaryReview}
                                            onChange={(event, checked) => {
                                                setChecklist(xValue => {
                                                    const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                                    nValue.blob = { ...nValue.blob! };;
                                                    nValue.blob.input = {
                                                        ...nValue.blob.input,
                                                        signatures: {
                                                            ...nValue.blob.input.signatures,
                                                            coordinator: checked
                                                                ? {
                                                                    type: 'entity',
                                                                    entityType: 'systemuser',
                                                                    id: systemUser.systemuserid!,
                                                                    name: systemUser.fullname!,
                                                                }
                                                                : undefined,
                                                            coordinatorDate: checked
                                                                ? {
                                                                    type: 'date',
                                                                    value: formatDate(new Date(), 'local:yyyy-MM-dd')
                                                                }
                                                                : undefined,
                                                        }
                                                    };
                                                    return nValue;
                                                });
                                            }}
                                        />;
                                    }}
                                    readOnly
                                />
                            </Stack.Item>
                        </Stack>
                    </Stack>
                </Stack.Item>

                <Stack.Item grow={1}>
                    <Stack>
                        <Label>Service Rep</Label>
                        <Stack horizontal>
                            <Stack.Item grow={1}>
                                <TextField
                                    styles={{ root: { width: '100%' } }}
                                    value={checklist.blob.input.signatures.serviceRep?.name}
                                    disabled={!isMarketingReview}
                                    onRenderSuffix={(textFieldProps, textFieldDefaultRender) => {
                                        return <TextField
                                            disabled={!isMarketingReview}
                                            value={checklist.blob.input.signatures.serviceRepDate?.value
                                                ? formatDate(parseDate(checklist.blob.input.signatures.serviceRepDate?.value, 'yyyy-MM-ddThh:mm:ssZ as local'), 'en-US-ET')
                                                : undefined}
                                            readOnly
                                        />;
                                    }}
                                    onRenderPrefix={(textFieldProps, textFieldDefaultRender) => {
                                        return <Checkbox
                                            disabled={!isMarketingReview}
                                            checked={!!checklist.blob.input.signatures.serviceRep?.id}
                                            onChange={(event, checked) => {
                                                setChecklist(xValue => {
                                                    const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                                    nValue.blob = { ...nValue.blob! };;
                                                    nValue.blob.input = {
                                                        ...nValue.blob.input,
                                                        signatures: {
                                                            ...nValue.blob.input.signatures,
                                                            serviceRep: checked
                                                                ? {
                                                                    type: 'entity',
                                                                    entityType: 'systemuser',
                                                                    id: systemUser.systemuserid!,
                                                                    name: systemUser.fullname!,
                                                                }
                                                                : undefined,
                                                            serviceRepDate: checked
                                                                ? {
                                                                    type: 'date',
                                                                    value: formatDate(new Date(), 'local:yyyy-MM-dd')
                                                                }
                                                                : undefined,
                                                        }
                                                    };
                                                    return nValue;
                                                });
                                            }}
                                        />;
                                    }}
                                    readOnly
                                />
                            </Stack.Item>
                        </Stack>
                    </Stack>
                </Stack.Item>

            </Stack>
        </CustomPanel>
    }

    const onRenderCheckListSummary = () => {
        if (!checklist)
            return;

        if (!checklist.blob)
            return;

        return <CustomPanel title="ID Card Verification Checklist">
            <Stack horizontal tokens={{ childrenGap: '20', }} wrap disableShrink horizontalAlign="space-between">

                <Stack.Item grow={1} shrink={1}>
                    <TextField
                        label={"Client's Name"}
                        value={checklist.blob.data.clientName}
                        disabled
                    />
                </Stack.Item>

                <Stack.Item grow={1} shrink={1}>
                    <TextField
                        label="Card Type"
                        value={checklist.blob.input.cardType}
                        disabled={!canEditFields}
                        onChange={(event, newValue) => {
                            setChecklist(xValue => {
                                const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                nValue.blob = { ...nValue.blob! };;
                                nValue.blob.input = { ...nValue.blob.input, cardType: newValue ?? '' };
                                return nValue;
                            });
                        }} />
                </Stack.Item>

                <Stack.Item grow={1} shrink={1}>
                    <TextField
                        label={"Admin"}
                        value={checklist.blob.data.admin?.name}
                        disabled
                    />
                </Stack.Item>

                <Stack.Item grow={1} shrink={1}>
                    <TextField
                        label="Date of Card Change"
                        value={checklist.blob.data.bcbs_effectivedate
                            ? formatDate(parseDate(checklist.blob.data.bcbs_effectivedate, 'yyyy-MM-ddThh:mm:ssZ as local'), 'local:MM/dd/yyyy')
                            : ''}
                        disabled
                    />
                </Stack.Item>

                <Stack.Item grow={1} shrink={1}>
                    <TextField
                        label="Service Rep"
                        value={checklist.blob.data.serviceRep?.name}
                        disabled

                    />
                </Stack.Item>

                <Stack.Item grow={1} shrink={1}>
                    <TextField
                        label={"Plan"}
                        value={checklist.blob.data.plan.name}
                        disabled
                    />
                </Stack.Item>

                <Stack.Item grow={1} shrink={1}>
                    <OptionControl
                        label="Relay Box"
                        disabled={!canEditFields}
                        disableClear
                        fieldValueOnChange={(_, selected) => {
                            setChecklist(xValue => {
                                const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                nValue.blob = { ...nValue.blob! };;
                                nValue.blob.input = { ...nValue.blob.input, relayBox: selected?.value };
                                return nValue;
                            });
                        }}
                        defaultFieldValue={checklist.blob.input.relayBox !== undefined
                            ? { type: 'bool', value: checklist.blob.input.relayBox ?? false }
                            : undefined}
                    />
                </Stack.Item>

                <Stack.Item grow={1} shrink={1}>
                    <Stack tokens={{ childrenGap: '10px' }}>
                        <TextField
                            label={"LOB"}
                            value={checklist.blob.data.lineOfBusiness?.name}
                            disabled
                        />
                        {checklist.blob.data.lineOfBusiness?.name === "National Alliance" &&
                            <Text variant="small">National Alliance (844-206-0621)</Text>
                        }
                        {checklist.blob.data.lineOfBusiness?.name === "Major Group" &&
                            <Text variant="small">Major Group (844-206-0620)</Text>
                        }
                    </Stack>
                </Stack.Item>

            </Stack>
        </CustomPanel>;
    }

    // const Mapping = {
    //     'BlueHPN': 'HPN',
    //     'Chamber': '',
    //     'Comprehensive': 'Comp',
    //     'Consumer Driven Health Plan (CDHP)': '',
    //     'Dental': '',
    //     'EPO (Limited OON)': 'EPO',
    //     'Exclusive Provider Organization (EPO)': 'EPO',
    //     'Flexible Spending Account (FSA)': '',
    //     'Freestanding Vision': '',
    //     'Health Reimbursement Account (HRA)': '',
    //     'Health Savings Account (HSA)': '',
    //     'Healthy Vision': '',
    //     'Integrated Rx w/o Mail Order': '',
    //     'Integrated Rx with Mail Order': '',
    //     'Integrated Rx without mail order': '',
    //     'Level Funded PPO': 'PPO',
    //     'Pharmacy Carve-In': '',
    //     'PPO': 'PPO',
    //     'Preferred Blue': '',
    //     'QHDHP (Qualified High Deductible Health Plan)': '',
    //     'Tiered EPO': 'EPO',
    //     'Tiered EPO (Limited OON)': 'EPO',
    //     'Tiered PPO': 'PPO',
    //     'True Blue Marketplace': '',
    // }


    /*
    Consumer Driven Health Plan (CDHP)
    Health Reimbursement Account (HRA)
    Health Savings Account (HSA)
    Integrated Rx w/o Mail Order
    Integrated Rx with Mail Order
    Integrated Rx without mail order
    Level Funded PPO
    PPO
    Preferred Blue
    QHDHP (Qualified High Deductible Health Plan)
    Tiered PPO
    */
    const isProductPPO = (checklist?.blob?.data?.bcbs_productsid)
        ? productsLookup.ppo[checklist?.blob?.data?.bcbs_productsid] // Regex: /\bPPO/.test(checklist?.blob?.data?.bcbs_productsid) || 
        : undefined;


    /*
    EPO (Limited OON)
    Exclusive Provider Organization (EPO)
    Tiered EPO
    Tiered EPO (Limited OON)
    */
    const isProductEPO = (checklist?.blob?.data?.bcbs_productsid)
        ? productsLookup.epo[checklist?.blob?.data?.bcbs_productsid] // Regex: /\bEPO/.test(checklist?.blob?.data?.bcbs_productsid)
        : undefined

    const isProductComprehensive = (checklist?.blob?.data?.bcbs_productsid)
        ? checklist.blob.data.bcbs_productsid === "Comprehensive"
        : undefined

    const isProductBlueHPN = (checklist?.blob?.data?.bcbs_productsid)
        ? checklist.blob.data.bcbs_productsid === "BlueHPN"
        : undefined

    const isProductDental = (checklist?.blob?.data?.bcbs_productsid)
        ? checklist.blob.data.bcbs_productsid === 'Dental'
        : undefined

    const isProductHealthyVision = (checklist?.blob?.data?.bcbs_productsid)
        ? checklist.blob.data.bcbs_productsid === 'Healthy Vision'
        : undefined


    const onRenderCheckListQuestions = () => {
        if (!checklist)
            return;

        if (!checklist.blob)
            return;

        /* 
            Partner Accounts:
            https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&pagetype=entityrecord&etn=account&id=621edbe7-d9ee-eb11-bacb-0022482191f6
            https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&pagetype=entityrecord&etn=account&id=381b1b71-d701-ec11-94ef-000d3a569466
            https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&pagetype=entityrecord&etn=account&id=1645f490-b7d9-ea11-a813-000d3a1b9df5 
        */
        const isPartnerPlan_VT_CA_KC = ['621edbe7-d9ee-eb11-bacb-0022482191f6', '381b1b71-d701-ec11-94ef-000d3a569466', '1645f490-b7d9-ea11-a813-000d3a1b9df5'].includes(checklist.blob.data.parentaccount?.id ?? '');
        const isPartnerPlan_VT_CA = ['621edbe7-d9ee-eb11-bacb-0022482191f6', '381b1b71-d701-ec11-94ef-000d3a569466'].includes(checklist.blob.data.parentaccount?.id ?? '');
        const isPartnerPlan_KC = ['1645f490-b7d9-ea11-a813-000d3a1b9df5'].includes(checklist.blob.data.parentaccount?.id ?? '');

        return <>
            {(
                checklist.bcbs_emchecklist.bcbs_primarynotes ||
                checklist.bcbs_emchecklist.bcbs_secondarynotes ||
                checklist.bcbs_emchecklist.bcbs_marketingnotes
            ) &&
                <Stack.Item>
                    <CustomPanel title="Notes">
                        {checklist.bcbs_emchecklist.bcbs_primarynotes &&
                            <TextField
                                label="Admin Coordinator Notes"
                                value={checklist.bcbs_emchecklist.bcbs_primarynotes}
                                readOnly
                                multiline
                                rows={5}
                                autoAdjustHeight={true}
                            />
                        }

                        {checklist.bcbs_emchecklist.bcbs_secondarynotes &&
                            <TextField
                                label="Coordinator Notes"
                                value={checklist.bcbs_emchecklist.bcbs_secondarynotes}
                                readOnly
                                multiline
                                rows={5}
                                autoAdjustHeight={true}
                            />
                        }

                        {checklist.bcbs_emchecklist.bcbs_marketingnotes &&
                            <TextField
                                label="ServiceRep Notes"
                                value={checklist.bcbs_emchecklist.bcbs_marketingnotes}
                                readOnly
                                multiline
                                rows={5}
                                autoAdjustHeight={true}
                            />
                        }

                    </CustomPanel>
                </Stack.Item>
            }

            <Stack.Item>
                <CustomPanel title="1.	Digital and Hardcopy Cards Match">
                    <OptionControl
                        label="Cards Match"
                        disableClear
                        disabled={!canEditFields}
                        fieldValueOnChange={(_, selected) => {
                            setChecklist(xValue => {
                                const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                nValue.blob = { ...nValue.blob! };
                                nValue.blob.input = {
                                    ...nValue.blob.input,
                                    Q1: {
                                        ...nValue.blob.input.Q1,
                                        yesNo: selected?.value
                                    }
                                }
                                return nValue;
                            });
                        }}
                        defaultFieldValue={checklist.blob.input.Q1.yesNo !== undefined
                            ? { type: 'bool', value: checklist.blob.input.Q1.yesNo ?? false }
                            : undefined
                        }
                    />
                    {onRenderInitials('Q1')}
                </CustomPanel>
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="2.	Card Carrier Verified (Correct Carrier, Partner Plan, etc)">
                    <OptionControl
                        label="Card Carrier Verified"
                        disableClear
                        disabled={!canEditFields}
                        fieldValueOnChange={(_, selected) => {
                            setChecklist(xValue => {
                                const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                nValue.blob = { ...nValue.blob! };
                                nValue.blob.input = {
                                    ...nValue.blob.input,
                                    Q2: {
                                        ...nValue.blob.input.Q2,
                                        yesNo: selected?.value
                                    }
                                }
                                return nValue;
                            });
                        }}
                        defaultFieldValue={checklist.blob.input.Q2.yesNo !== undefined
                            ? { type: 'bool', value: checklist.blob.input.Q2.yesNo ?? false }
                            : undefined
                        }
                    />
                    {onRenderInitials('Q2')}
                </CustomPanel>
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="3.	BCBS Logo">
                    <TextField
                        label="BCBS Logo"
                        value={mapPrefixToLogo(checklist.blob.data.prefix)}
                        disabled
                    />
                    {onRenderInitials('Q3')}
                </CustomPanel>
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="4.	Partner Plan">
                    <TextField
                        label="Partner Plan"
                        value={checklist.blob.data.parentaccount?.name}
                        disabled
                    />
                    {onRenderInitials('Q4')}
                </CustomPanel>
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="5.	Custom Group Logo">
                    <OptionControl
                        label="Custom Group Logo"
                        defaultFieldValue={checklist.blob.input.Q5.yesNo !== undefined
                            ? {
                                type: 'bool',
                                value: checklist.blob.input.Q5.yesNo ?? false
                            }
                            : undefined
                        }
                        fieldValueOnChange={(_, selected) => {
                            setChecklist(xValue => {
                                const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                nValue.blob = { ...nValue.blob! };
                                nValue.blob.input = {
                                    ...nValue.blob.input,
                                    Q5: {
                                        ...nValue.blob.input.Q5,
                                        yesNo: selected?.value
                                    }
                                }
                                return nValue;
                            });
                        }}

                        disabled={!canEditFields}

                    />
                    {onRenderInitials('Q5')}
                </CustomPanel>
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="6.	QR Code (Marketing should scan code to verify)">
                    <OptionControl
                        label="QR Code"
                        disabled={!canEditFields}
                        fieldValueOnChange={(_, selected) => {
                            setChecklist(xValue => {
                                const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                nValue.blob = { ...nValue.blob! };
                                nValue.blob.input = {
                                    ...nValue.blob.input,
                                    Q6: {
                                        ...nValue.blob.input.Q6,
                                        yesNo: selected?.value
                                    }
                                }
                                return nValue;
                            });
                        }}
                        defaultFieldValue={checklist.blob.input.Q6.yesNo !== undefined
                            ? {
                                type: 'bool',
                                value: checklist.blob.input.Q6.yesNo ?? false
                            }
                            : undefined
                        }
                    />
                    {onRenderInitials('Q6')}
                </CustomPanel>
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="7.	Mailing Location">
                    <Stack>
                        <TextField
                            label="Initial"
                            value={checklist.blob.data.bcbs_sendinitialcardsto}
                            disabled
                        />
                        <TextField
                            label="Ongoing"
                            value={checklist.blob.data.bcbs_aftersetupsendcardsto}
                            disabled
                        />
                    </Stack>
                    {onRenderInitials('Q7')}
                </CustomPanel>
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="8.	Alt Network & Alpha Prefix">
                    <Stack tokens={{ childrenGap: '10px' }}>
                        {checklist.blob.data.network_alphaprefixes?.map(q => {
                            return <Checkbox
                                key={q}
                                label={q}
                                checked={true}
                                disabled
                            />;
                        })}
                    </Stack>
                    {onRenderInitials('Q8')}
                </CustomPanel>
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="9.	Suitcase Options">
                    <Stack tokens={{ childrenGap: '10px' }}>
                        <Checkbox
                            label="PPO/EPO (PPO in Suitcase)"
                            checked={isProductPPO || isProductEPO}
                            disabled
                        />
                        <Checkbox
                            label="Comp (Empty Suitcase)"
                            checked={isProductComprehensive}
                            disabled
                        />
                        <Checkbox
                            label="Blue HPN (HPN in Suitcase)"
                            checked={isProductBlueHPN}
                            disabled
                        />
                        <Checkbox
                            label="Dental or Vision Plan (No suitcase)"
                            checked={(
                                !isProductPPO &&
                                !isProductEPO &&
                                !isProductComprehensive &&
                                !isProductBlueHPN
                            )}
                            disabled
                        />
                    </Stack>
                    {onRenderInitials('Q9')}
                </CustomPanel >
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="10.	Plan/Rx Info">

                    {/* RxBin and RxGRP values should both be displayed if this field = BCBSRX. */}
                    <Stack tokens={{ childrenGap: '10px' }}>
                        <Checkbox
                            label="RxBIN 021684 (BCBS Rx Only)"
                            checked={checklist.blob.data.bcbs_epo_drgugsarecoveredunder === 'BCBS Rx' || !!checklist.blob.data.bcbs_pbm}
                            disabled
                        />

                        <Checkbox
                            label="BXMN (BCBS Rx Only)"
                            checked={checklist.blob.data.bcbs_epo_drgugsarecoveredunder === 'BCBS Rx' || !!checklist.blob.data.bcbs_pbm}
                            disabled
                        />

                        {/* Custom RX needs to be an open text field.  Info does not live in the library.  Custom Rx should always be displayed */}
                        <TextField
                            label="Custom Rx"
                            value={checklist.blob.input.Q10.customRX}
                            disabled={!canEditFields}
                            onChange={(event, newValue) => {
                                setChecklist(xValue => {
                                    const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                    nValue.blob = { ...nValue.blob! };;
                                    nValue.blob.input = {
                                        ...nValue.blob.input,
                                        Q10: {
                                            ...nValue.blob.input.Q10,
                                            customRX: newValue
                                        }
                                    };
                                    return nValue;
                                });
                            }}

                        />

                        {onRenderInitials('Q10')}

                    </Stack>
                </CustomPanel>
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="11.	Custom/Special Information">
                    <Stack tokens={{ childrenGap: '10px' }}>

                        <Checkbox
                            label="N/A"
                            onChange={(event, newValue) => {
                                setChecklist(xValue => {
                                    const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                    nValue.blob = { ...nValue.blob! };;
                                    nValue.blob.input = {
                                        ...nValue.blob.input,
                                        Q11: {
                                            ...nValue.blob.input.Q11,
                                            hasNA: newValue
                                        }
                                    };
                                    return nValue;
                                });
                            }}
                        />

                        <Checkbox
                            label="GRID+ (only on groups that choose Dental)"
                            checked={!!checklist.blob.data.bcbs_dentalplantypeid}
                            disabled
                        />

                        <Checkbox
                            label="Quantum Verbiage (bottom left corner)"
                            checked={checklist.blob.data.bcbs_externalservicesvendorid === "Quantum"}
                            disabled
                        />

                        <Checkbox
                            label="Mammography Network (Major Group only)"
                            // Client requested to make the field user based entry
                            // checked={checklist.blob.data.bcbs_mammographynetworkprovider}
                            onChange={(event, newValue) => {
                                setChecklist(xValue => {
                                    const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                    nValue.blob = { ...nValue.blob! };;
                                    nValue.blob.input = {
                                        ...nValue.blob.input,
                                        Q11: {
                                            ...nValue.blob.input.Q11,
                                            mammographyNetwork: newValue
                                        }
                                    };
                                    return nValue;
                                });
                            }}
                        />

                        <CheckBoxWithTextField
                            label='Plan Name'
                            textFieldProps={{
                                value: checklist.blob.input.Q11.planName,
                                disabled: !checklist.blob.input.Q11.bcbs_planname || !canEditFields,
                                onChange: (event, newValue) => {
                                    setChecklist(xValue => {
                                        const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                        nValue.blob = { ...nValue.blob! };;
                                        nValue.blob.input = {
                                            ...nValue.blob.input,
                                            Q11: {
                                                ...nValue.blob.input.Q11,
                                                planName: newValue
                                            }
                                        };
                                        return nValue;

                                    });
                                }
                            }}
                            checkboxProps={{
                                checked: checklist.blob.input.Q11.bcbs_planname,
                                disabled: !canEditFields,
                                onChange: (event, newValue) => {
                                    setChecklist(xValue => {
                                        const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                        nValue.blob = { ...nValue.blob! };;
                                        nValue.blob.input = {
                                            ...nValue.blob.input,
                                            Q11: {
                                                ...nValue.blob.input.Q11,
                                                bcbs_planname: !!newValue,
                                                planName: newValue ? nValue.blob.input.Q11.planName : '',
                                            }
                                        };
                                        return nValue;
                                    });
                                }
                            }}
                        />

                        <CheckBoxWithTextField
                            label='Other'
                            textFieldProps={{
                                value: checklist.blob.input.Q11.other,
                                disabled: !checklist.blob.input.Q11.hasOther || !canEditFields,
                                onChange: (event, newValue) => {
                                    setChecklist(xValue => {
                                        const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                        nValue.blob = { ...nValue.blob! };;
                                        nValue.blob.input = {
                                            ...nValue.blob.input,
                                            Q11: {
                                                ...nValue.blob.input.Q11,
                                                other: newValue
                                            }
                                        };
                                        return nValue;
                                    });
                                }
                            }}
                            checkboxProps={{
                                checked: checklist.blob.input.Q11.hasOther,
                                disabled: !canEditFields,
                                onChange: (event, newValue) => {
                                    setChecklist(xValue => {
                                        const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                        nValue.blob = { ...nValue.blob! };;
                                        nValue.blob.input = {
                                            ...nValue.blob.input,
                                            Q11: {
                                                ...nValue.blob.input.Q11,
                                                hasOther: newValue
                                            }
                                        };
                                        return nValue;
                                    });
                                }
                            }}
                        />

                        {onRenderInitials('Q11')}
                    </Stack>

                </CustomPanel>
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="12.	Deductible/OOP CAA Information">
                    <DetailsList
                        items={checklist.blob.data.deductibles}
                        columns={deductibleColumns}
                        compact={true}
                        layoutMode={DetailsListLayoutMode.fixedColumns}
                        constrainMode={ConstrainMode.horizontalConstrained}
                        onShouldVirtualize={(props) => false}
                        selectionMode={SelectionMode.none}
                        styles={{
                            headerWrapper: {
                                selectors: {
                                    '.ms-DetailsHeader': {
                                        paddingTop: '0px',
                                    },
                                }
                            }
                        }}
                    />
                    {onRenderInitials('Q12')}

                </CustomPanel>
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="13.	PPO, EPO or Blue HPN Verbiage">
                    <Stack tokens={{ childrenGap: '10px' }}>
                        <Checkbox
                            label="PPO Verbiage   Members: Call Customer Service for claims filing information."
                            disabled
                            checked={isProductPPO || isProductComprehensive}
                        />
                        <Checkbox
                            label="EPO Verbiage - This is an EPO plan. Members have limited out-of-area benefits, and any benefits are only available when receiving services from a BlueCard PPO network provider."
                            disabled
                            checked={isProductEPO}
                        />
                        <Checkbox
                            label="Blue HPN Verbiage (bottom left-hand side) -  PPO benefit limited to emergent care at non-Blue HPN providers within Blue HPN product areas. PPO benefit limited to urgent and emergent care at non-Blue HPN providers outside of the Blue HPN product areas."
                            disabled
                            checked={isProductBlueHPN}
                        />
                        <Checkbox
                            label="N/A"
                            disabled
                            checked={!isProductBlueHPN && !isProductEPO && !isProductPPO && !isProductComprehensive}
                        />
                        {onRenderInitials('Q13')}
                    </Stack>
                </CustomPanel>
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="14.	Preauthorization Requirements">
                    <Stack tokens={{ childrenGap: '10px' }}>
                        <Checkbox
                            label="Inpatient Only"
                            checked={checklist.blob.data.bcbs_ispreauthorizationrequiredforinpatientfac && !checklist.blob.data.bcbs_ispreauthorizationrequiredforoutpatientfa}
                            disabled
                        />
                        <Checkbox
                            label="Outpatient Only"
                            checked={!checklist.blob.data.bcbs_ispreauthorizationrequiredforinpatientfac && checklist.blob.data.bcbs_ispreauthorizationrequiredforoutpatientfa}
                            disabled
                        />
                        <Checkbox
                            label="Both inpatient and outpatient"
                            checked={
                                (checklist.blob.data.fundingType?.name === "Fully Insured") ||
                                (checklist.blob.data.bcbs_ispreauthorizationrequiredforinpatientfac && checklist.blob.data.bcbs_ispreauthorizationrequiredforoutpatientfa)
                            }
                            disabled
                        />
                        <Checkbox
                            label="No preauthorization required"
                            checked={
                                !(checklist.blob.data.fundingType?.name === "Fully Insured") &&
                                !checklist.blob.data.bcbs_ispreauthorizationrequiredforinpatientfac &&
                                !checklist.blob.data.bcbs_ispreauthorizationrequiredforoutpatientfa
                            }
                            disabled
                        />

                        <Checkbox
                            label="Accolade"
                            checked={checklist.blob.data.bcbs_externalservicesvendorid === "Accolade"}
                            disabled
                        />

                        <Checkbox
                            label="Quantum"
                            checked={checklist.blob.data.bcbs_externalservicesvendorid === "Quantum"}
                            disabled
                        />

                        <Checkbox
                            label="RxPreAuth (SMBM)"
                            checked={checklist.blob.data.bcbs_rxpreauth_smbm ||
                                checklist.blob.data.bcbs_novologix ||
                                checklist.blob.data.fundingType?.name === "Fully Insured"
                            }
                            disabled={true}
                        />

                        <CheckBoxWithTextField
                            label='Custom'

                            textFieldProps={{
                                value: checklist.blob.input.Q14.custom,
                                disabled: !checklist.blob.input.Q14.hasCustom || !canEditFields,
                                onChange: (event, newValue) => {
                                    setChecklist(xValue => {
                                        const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                        nValue.blob = { ...nValue.blob! };;
                                        nValue.blob.input = {
                                            ...nValue.blob.input,
                                            Q14: {
                                                ...nValue.blob.input.Q14,
                                                custom: newValue
                                            }
                                        };
                                        return nValue;
                                    });
                                }
                            }}
                            checkboxProps={{
                                checked: checklist.blob.input.Q14.hasCustom,
                                disabled: !canEditFields,
                                onChange: (event, newValue) => {
                                    setChecklist(xValue => {
                                        const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                        nValue.blob = { ...nValue.blob! };;
                                        nValue.blob.input = {
                                            ...nValue.blob.input,
                                            Q14: {
                                                ...nValue.blob.input.Q14,
                                                hasCustom: newValue
                                            }
                                        };
                                        return nValue;
                                    });
                                }
                            }}
                        />
                        {onRenderInitials('Q14')}
                    </Stack>
                </CustomPanel>
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="15.	National Imaging Associates (NIA)">
                    <Stack tokens={{ childrenGap: '10px' }}>
                        <Checkbox
                            label="No NIA"
                            checked={
                                checklist.blob.data.fundingType?.name !== "Fully Insured" &&
                                (checklist.blob.data.bcbs_nia_programs === "No" || checklist.blob.data?.bcbs_nia_programs_split?.length === 0)
                            }
                            disabled
                        />
                        <Checkbox
                            label="Radiology Management"
                            checked={checklist.blob.data.bcbs_nia_programs_split?.includes("Radiology Management") || checklist.blob.data.fundingType?.name === "Fully Insured"}
                            disabled
                        />
                        <Checkbox
                            label="Radiation Oncology"
                            checked={checklist.blob.data.bcbs_nia_programs_split?.includes("Radiation Oncology") || checklist.blob.data.fundingType?.name === "Fully Insured"}
                            disabled

                        />
                        <Checkbox
                            label="Musculoskeletal Care"
                            checked={checklist.blob.data.bcbs_nia_programs_split?.includes("Musculoskeletal Care")}
                            disabled
                        />
                        {onRenderInitials('Q15')}
                    </Stack>
                </CustomPanel>
            </Stack.Item>

            <Stack.Item>
                <CustomPanel title="16.	Other Phone Numbers">
                    <Stack tokens={{ childrenGap: '10px' }}>

                        <TextField
                            label="Customer Service Phone Number"
                            value={checklist.blob.data.bcbs_servicecenterphone}
                            disabled
                        />


                        <CustomPanel
                            title="Quantum Groups"
                            onRenderHeaderTitle={() => {
                                return <Checkbox
                                    styles={{ root: { marginLeft: '10px' } }}
                                    label="Quantum Groups"
                                    checked={checklist.blob.data.bcbs_externalservicesvendorid === "Quantum"}
                                    disabled
                                />
                            }}
                        >
                            <Stack tokens={{ childrenGap: '10px' }}>

                                <Stack horizontal tokens={{ childrenGap: '10px' }}>
                                    <Stack.Item grow={1}>
                                        <CheckBoxWithTextField
                                            label='Provider Services'
                                            textFieldProps={{
                                                value: checklist.blob.data.bcbs_quantum_providernumber,
                                                disabled: true,
                                            }}
                                            checkboxProps={{
                                                checked: !!checklist.blob.data.bcbs_quantum_providernumber,
                                                disabled: true,
                                            }}
                                        />
                                    </Stack.Item>
                                    <Stack.Item grow={1}>
                                        <CheckBoxWithTextField
                                            label='Customer Service'
                                            textFieldProps={{
                                                value: checklist.blob.data.bcbs_quantum_csnumber,
                                                disabled: true,
                                            }}
                                            checkboxProps={{
                                                checked: !!checklist.blob.data.bcbs_quantum_csnumber,
                                                disabled: true,
                                            }}
                                        />
                                    </Stack.Item>
                                </Stack>
                                <Stack horizontal tokens={{ childrenGap: '10px' }}>
                                    <Stack.Item grow={1}>
                                        <CheckBoxWithTextField
                                            label='URL'
                                            textFieldProps={{
                                                value: checklist.blob.data.bcbs_quantum_url,
                                                disabled: true,
                                            }}
                                            checkboxProps={{
                                                checked: !!checklist.blob.data.bcbs_quantum_url,
                                                disabled: true,
                                            }}
                                        />
                                    </Stack.Item>
                                </Stack>
                            </Stack>

                        </CustomPanel>

                        <CustomPanel
                            title="Accolade Groups"
                            onRenderHeaderTitle={() => {
                                return <Checkbox
                                    styles={{ root: { marginLeft: '10px' } }}
                                    checked={checklist.blob.data.bcbs_externalservicesvendorid === "Accolade"}
                                    label="Accolade Groups"
                                    disabled
                                />
                            }}
                        >
                            <Stack tokens={{ childrenGap: '10px' }}>
                                <Stack horizontal tokens={{ childrenGap: '10px' }}>
                                    <Stack.Item grow={1}>
                                        <CheckBoxWithTextField
                                            label='Provider Services'
                                            textFieldProps={{
                                                value: checklist.blob.data.bcbs_acccolade_providernumber,
                                                disabled: true,
                                            }}
                                            checkboxProps={{
                                                checked: !!checklist.blob.data.bcbs_acccolade_providernumber,
                                                disabled: true,
                                            }}
                                        />
                                    </Stack.Item>
                                    <Stack.Item grow={1}>
                                        <CheckBoxWithTextField
                                            label='Customer Service'
                                            textFieldProps={{
                                                value: checklist.blob.data.bcbs_accolade_csnumber,
                                                disabled: true,
                                            }}
                                            checkboxProps={{
                                                checked: !!checklist.blob.data.bcbs_accolade_csnumber,
                                                disabled: true,
                                            }}
                                        />
                                    </Stack.Item>
                                </Stack>
                                <Stack horizontal tokens={{ childrenGap: '10px' }}>
                                    <Stack.Item grow={1}>
                                        <CheckBoxWithTextField
                                            label='URL'
                                            textFieldProps={{
                                                value: checklist.blob.data.bcbs_accolade_url,
                                                disabled: true,
                                            }}
                                            checkboxProps={{
                                                checked: !!checklist.blob.data.bcbs_accolade_url,
                                                disabled: true,
                                            }}
                                        />
                                    </Stack.Item>
                                </Stack>
                            </Stack>


                        </CustomPanel>


                        <CustomPanel
                            title="HealthCheck360 Groups"
                            onRenderHeaderTitle={() => {
                                return <Checkbox
                                    styles={{ root: { marginLeft: '10px' } }}
                                    checked={checklist.blob.data.bcbs_externalservicesvendorid === "HealthCheck360"}
                                    label="HealthCheck360 Groups"
                                    disabled
                                />
                            }}
                        >
                            <Stack tokens={{ childrenGap: '10px' }}>
                                <Stack horizontal tokens={{ childrenGap: '10px' }}>
                                    <Stack.Item grow={1}>
                                        <CheckBoxWithTextField
                                            label='Provider Services'
                                            textFieldProps={{
                                                value: checklist.blob.data.bcbs_healthcheck360_providernumber,
                                                disabled: true,
                                            }}
                                            checkboxProps={{
                                                checked: !!checklist.blob.data.bcbs_healthcheck360_providernumber,
                                                disabled: true,
                                            }}
                                        />
                                    </Stack.Item>
                                    <Stack.Item grow={1}>
                                        <CheckBoxWithTextField
                                            label='Customer Service'
                                            textFieldProps={{
                                                value: checklist.blob.data.bcbs_healthcheck360_csnumber,
                                                disabled: true,
                                            }}
                                            checkboxProps={{
                                                checked: !!checklist.blob.data.bcbs_healthcheck360_csnumber,
                                                disabled: true,
                                            }}
                                        />
                                    </Stack.Item>
                                </Stack>
                                <Stack horizontal tokens={{ childrenGap: '10px' }}>
                                    <Stack.Item grow={1}>
                                        <CheckBoxWithTextField
                                            label='URL'
                                            textFieldProps={{
                                                value: checklist.blob.data.bcbs_healthcheck360_url,
                                                disabled: true,
                                            }}
                                            checkboxProps={{
                                                checked: !!checklist.blob.data.bcbs_healthcheck360_url,
                                                disabled: true,
                                            }}
                                        />
                                    </Stack.Item>
                                </Stack>
                            </Stack>

                        </CustomPanel>

                        {/* <CustomPanel
                            title="Included Health Groups"
                            onRenderHeaderTitle={() => {
                                return <Checkbox
                                    styles={{ root: { marginLeft: '10px' } }}
                                    checked={checklist.blob.data.bcbs_externalservicesvendorid === "Included Health"}
                                    label="Included Health Groups"
                                    disabled
                                />
                            }}
                        >
                            <Stack tokens={{ childrenGap: '10px' }}>
                                <Stack horizontal tokens={{ childrenGap: '10px' }}>
                                    <Stack.Item grow={1}>
                                        <CheckBoxWithTextField
                                            label='Provider Services'
                                            textFieldProps={{
                                                value: checklist.blob.data.bcbs_includedhealth_providernumber,
                                                disabled: true,
                                            }}
                                            checkboxProps={{
                                                checked: !!checklist.blob.data.bcbs_includedhealth_providernumber,
                                                disabled: true,
                                            }}
                                        />
                                    </Stack.Item>
                                    <Stack.Item grow={1}>
                                        <CheckBoxWithTextField
                                            label='Customer Service'
                                            textFieldProps={{
                                                value: checklist.blob.data.bcbs_includedhealth_csnumber,
                                                disabled: true,
                                            }}
                                            checkboxProps={{
                                                checked: !!checklist.blob.data.bcbs_includedhealth_csnumber,
                                                disabled: true,
                                            }}
                                        />
                                    </Stack.Item>
                                </Stack>
                                <Stack horizontal tokens={{ childrenGap: '10px' }}>
                                    <Stack.Item grow={1}>
                                        <CheckBoxWithTextField
                                            label='URL'
                                            textFieldProps={{
                                                value: checklist.blob.data.bcbs_includedhealth_url,
                                                disabled: true,
                                            }}
                                            checkboxProps={{
                                                checked: !!checklist.blob.data.bcbs_includedhealth_url,
                                                disabled: true,
                                            }}
                                        />
                                    </Stack.Item>
                                </Stack>
                            </Stack>
                        </CustomPanel> */}



                        <CustomPanel title="Additional Numbers">
                            <Stack tokens={{ childrenGap: '10px' }}>
                                <Checkbox
                                    label="PPO Network Provider Information (does not appear on Comp cards): 800-810-2583"
                                    checked={!isProductDental && !isProductHealthyVision && !isProductComprehensive}
                                    disabled
                                />
                                <Checkbox
                                    label="Provider Service: 800-868-2510"
                                    checked={checklist.blob.data.lineOfBusiness?.name === "National Alliance" && !isPartnerPlan_VT_CA_KC}
                                    disabled
                                />
                                <Checkbox
                                    label="Provider Service: 833-644-1297 (used for VT, Capital and KC hospital clients)"
                                    checked={!!checklist.blob.data.medicalBenefit && (isPartnerPlan_VT_CA || isPartnerPlan_KC) && ((checklist.blob.data.medicalBenefitTiers?.length ?? 0) > 2)}
                                    disabled
                                />
                                <Checkbox
                                    label="Precertification - FI: 800-334-7287"
                                    checked={checklist.blob.data.fundingType?.name === "Fully Insured"}
                                    disabled
                                />
                                <Checkbox
                                    label="Precertification - Major Group: 800-334-7287"
                                    checked={checklist.blob.data.lineOfBusiness?.name === "Major Group" && (checklist.blob.data.bcbs_ispreauthorizationrequiredforinpatientfac || checklist.blob.data.bcbs_ispreauthorizationrequiredforoutpatientfa)}
                                    disabled
                                />
                                <Checkbox
                                    label="Precertification - National Alliance: 888-376-6544"
                                    checked={checklist.blob.data.lineOfBusiness?.name === "National Alliance" && (checklist.blob.data.bcbs_ispreauthorizationrequiredforinpatientfac || checklist.blob.data.bcbs_ispreauthorizationrequiredforoutpatientfa)}
                                    disabled
                                />
                                <Checkbox
                                    label="Mental Health Substance Abuse Precertification (if group has CBA): 800-868-1032"
                                    checked={checklist.blob.data.bcbs_doescompanionbenefitalternativecbamanagep || checklist.blob.data.fundingType?.name === "Fully Insured"}
                                    disabled
                                />
                                <Checkbox
                                    label="Pharmacy Help Desk: 855-811-2218"
                                    checked={checklist.blob.data.bcbs_epo_drgugsarecoveredunder === "BCBS Rx" || !!checklist.blob.data.bcbs_pbm}
                                    disabled
                                />
                                <Checkbox
                                    label="(RxPreAuth - SMBM) Buy and Bill drugs - Precertification: 877-440-0089"
                                    checked={
                                        checklist.blob.data.bcbs_rxpreauth_smbm ||
                                        checklist.blob.data.bcbs_novologix ||
                                        checklist.blob.data.fundingType?.name === "Fully Insured"
                                    }
                                    disabled
                                />
                                <Checkbox
                                    label="Dental (groups without custom numbers): 800-222-7156"
                                    checked={!!checklist.blob.data.bcbs_dentalplantypeid && checklist.blob.data.bcbs_grpconfigservicecenterclaimsprocessing !== "Greenville"}
                                    disabled
                                // onChange={(event, newValue) => {
                                //     setChecklist(xValue => {
                                //         const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                //         nValue.blob = { ...nValue.blob! };;
                                //         nValue.blob.input = {
                                //             ...nValue.blob.input,
                                //             Q16: {
                                //                 ...nValue.blob.input.Q16,
                                //                 hasDental: newValue
                                //             }
                                //         };
                                //         return nValue;
                                //     });
                                // }}
                                />
                                <Checkbox
                                    label="Eyemed: 866-939-3633"
                                    disabled
                                    checked={checklist.blob.data.bcbs_epo_rountinevisioncovered === 'Healthy Vision' || !!checklist.blob.data.bcbs_visionproducttype}
                                />
                                <Checkbox
                                    label="Teladoc: 866-789-8155"
                                    disabled={!canEditFields}
                                    checked={checklist.blob.input.Q16.hasTeladoc}
                                    onChange={(event, newValue) => {
                                        setChecklist(xValue => {
                                            const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                            nValue.blob = { ...nValue.blob! };;
                                            nValue.blob.input = {
                                                ...nValue.blob.input,
                                                Q16: {
                                                    ...nValue.blob.input.Q16,
                                                    hasTeladoc: newValue
                                                }
                                            };
                                            return nValue;
                                        })
                                    }}
                                />
                                <Checkbox
                                    label="24 Hour Nurse: 877-836-0701"
                                    disabled
                                    checked={checklist.blob.data.bcbs_ancben24hournurseadvisor}
                                />
                                <Checkbox
                                    label="Essential Advocate: 855-638-5839"
                                    disabled
                                    checked={checklist.blob.data.bcbs_essentialadvocate}
                                />

                                <CheckBoxWithTextField
                                    label='Other'
                                    textFieldProps={{
                                        value: checklist.blob.input.Q16.other,
                                        disabled: !checklist.blob.input.Q16.hasOther || !canEditFields,
                                        onChange: (event, newValue) => {
                                            setChecklist(xValue => {
                                                const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                                nValue.blob = { ...nValue.blob! };;
                                                nValue.blob.input = {
                                                    ...nValue.blob.input,
                                                    Q16: {
                                                        ...nValue.blob.input.Q16,
                                                        other: newValue
                                                    }
                                                };
                                                return nValue;
                                            });
                                        }
                                    }}
                                    checkboxProps={{
                                        checked: checklist.blob.input.Q16.hasOther,
                                        disabled: !canEditFields,
                                        onChange: (event, newValue) => {
                                            setChecklist(xValue => {
                                                const nValue: ICheckList = { ...xValue!, modifiedOn: new Date().getTime() };
                                                nValue.blob = { ...nValue.blob! };;
                                                nValue.blob.input = {
                                                    ...nValue.blob.input,
                                                    Q16: {
                                                        ...nValue.blob.input.Q16,
                                                        hasOther: newValue
                                                    }
                                                };
                                                return nValue;
                                            });
                                        }
                                    }}
                                />
                            </Stack>
                        </CustomPanel>
                        {onRenderInitials('Q16')}
                    </Stack>
                </CustomPanel>
            </Stack.Item>

        </>
    }

    const onRenderCheckList = () => {
        return <>
            {checklistLoading &&
                <ProgressIndicator />
            }
            <Stack>
                <Stack.Item>
                    {onRenderCheckListSummary()}
                </Stack.Item>
                {onRenderCheckListQuestions()}
                <Stack.Item>
                    {onRenderCheckListSignature()}
                </Stack.Item>
            </Stack>
        </>
    }

    const onRenderPDFViewer = () => {
        return <>
            {pdfLoading &&
                <ProgressIndicator />
            }
            <iframe
                style={{
                    width: '100%',
                    height: window.innerHeight - 125,
                    border: 'none',
                    // position: 'fixed'
                }}

                // src="./simpleviewer.html"
                src={documentFile
                    ? `./generic/web/viewer.html?file=${encodeURIComponent(documentFile)}`
                    : `./generic/web/viewer.html`
                }
            />
        </>;
    }

    const onRenderDocumentManagement = () => {
        if (!checklist)
            return;

        return <DocumentManagement
            isOpen={isOpenDoc || !!mergedDocument}
            onCancel={() => {
                setIsOpenDoc(false);
                setMergedDocument(undefined)
            }}
            planId={checklist?.bcbs_emchecklist.bcbs_planid!}
            documents={checklist.blob?.documents}
            mergeDocument={mergedDocument}
            onChange={(documents) => {
                setDocumentFile(null);
                setMergedDocument(undefined);
                setChecklist(xValue => {
                    const nData: ICheckList = {
                        ...xValue!,
                        modifiedOn: new Date().getTime(),
                        blob: {
                            ...xValue?.blob!,
                            documents: documents
                        }
                    }
                    return nData;
                });
                setIsOpenDoc(false);
            }}
        />;
    }

    const onRenderValidationDialog = () => {
        if (saveTransaction?.validation?.state === EValidationState.PendingUser)
            return <ValidationDialog
                defaultValue={saveTransaction?.validation}
                onCancel={(xValidationModel) => {
                    const newSaveInstructions = { ...saveTransaction };
                    newSaveInstructions.validation = {
                        ...newSaveInstructions.validation!,
                        state: EValidationState.Rejected
                    };

                    Save(newSaveInstructions);
                }}
                onContinue={(xValidationModel) => {

                    const newSaveInstructions = { ...saveTransaction };

                    newSaveInstructions.validation = {
                        ...newSaveInstructions.validation!,
                        state: EValidationState.Fulfilled,
                    }

                    Save(newSaveInstructions);
                }}
            />
    }

    const onRenderCommentDialog = () => {
        if (!checklist)
            return;

        let noteDefaultValue: string | undefined = undefined;

        // anything from transaction/previous attempts?
        if (!noteDefaultValue)
            if (saveTransaction?.bcbs_emchecklist_updates)
                if (isPrimaryReview || isPendingIssues)
                    noteDefaultValue = saveTransaction?.bcbs_emchecklist_updates?.bcbs_primarynotes;
                else if (isSecondaryReview)
                    noteDefaultValue = saveTransaction?.bcbs_emchecklist_updates?.bcbs_secondarynotes;
                else if (isMarketingReview)
                    noteDefaultValue = saveTransaction?.bcbs_emchecklist_updates?.bcbs_marketingnotes;

        if (!noteDefaultValue)
            if (isPrimaryReview || isPendingIssues)
                noteDefaultValue = noteDefaultValue ?? checklist.bcbs_emchecklist.bcbs_primarynotes;
            else if (isSecondaryReview)
                noteDefaultValue = noteDefaultValue ?? checklist.bcbs_emchecklist.bcbs_secondarynotes;
            else if (isMarketingReview)
                noteDefaultValue = noteDefaultValue ?? checklist.bcbs_emchecklist.bcbs_marketingnotes;


        return <CommentDialog
            isOpen={saveTransaction?.pendingComment ?? false}
            defaultValue={noteDefaultValue}
            onConfirm={(note) => {
                if (saveTransaction) {

                    const newSaveInstructions: ISaveTransaction = { ...saveTransaction };

                    newSaveInstructions.bcbs_emchecklist_updates = { ...newSaveInstructions.bcbs_emchecklist_updates };

                    if (isPrimaryReview || isPendingIssues)
                        newSaveInstructions.bcbs_emchecklist_updates.bcbs_primarynotes = note;
                    else if (isSecondaryReview)
                        newSaveInstructions.bcbs_emchecklist_updates.bcbs_secondarynotes = note;
                    else if (isMarketingReview)
                        newSaveInstructions.bcbs_emchecklist_updates.bcbs_marketingnotes = note;

                    newSaveInstructions.pendingComment = false;

                    Save(newSaveInstructions)
                }
            }}
            onDismiss={() => {
                setSaveTransaction(null);
            }}
        />
    }

    const onRenderView = () => {
        if (detachedViewer)
            return onRenderCheckList();
        else
            return <ScreenSplitter >
                <div style={{ height: window.innerHeight - 125, overflowY: 'scroll', paddingRight: '6px' }}>
                    {onRenderCheckList()}
                </div>
                <div>
                    {onRenderPDFViewer()}
                </div>
            </ScreenSplitter>
    }

    const onRenderPrompt = () => {
        return <Prompt
            when={(checklist?.modifiedOn ?? 0) > checkpointOn}
            message={'Changes you made may not be saved?'}
        />
    }

    return <BlueRelationshipContext contextMenu={contextMenu} key={renderIndex} isChecklist={true}>
        {onRenderDocumentManagement()}
        {onRenderView()}
        {onRenderCommentDialog()}
        {onRenderPrompt()}
        {onRenderValidationDialog()}
    </BlueRelationshipContext>
}

export default CheckListItem;